import { type FormEvent, Fragment, type JSX, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  anlegen,
  belegErzeugen,
  herabsetzen,
  stornoZuruecknehmen,
  buchbareTermine,
  teilnehmerMelden,
  teilnehmerZurBuchung,
  fuehreAus,
  teilnehmerliste as ladeTeilnehmer,
  ueberNummer,
  vorgaenge as ladeVorgaenge,
  vorschau as ladeVorschau,
  VORGANGSNAME,
  type Buchungsauskunft,
  type Teilnehmerstand,
  type Teilnehmerzeile,
  type Terminauswahl,
  type Vorgangsart,
  type Vorgangszeile,
  type Vorschau,
  zusatzleistungen as ladeZusatz,
  zusatzleistungPruefen as zusatzPruefen,
  zusatzleistungWaehlen as zusatzWaehle,
  zusatzleistungStornieren as zusatzStorniere,
  preisauskunft as ladePreis,
  type Preisauskunft as Preisangabe,
  zusatzangebotZurBuchung,
  zusatzpreislisteSetzen,
  type Zusatzpreisliste,
  type Einwand,
  type Verdraengt,
  type Zusatzleistung,
  uebersicht as ladeUebersicht,
  belegpositionenErzeugen,
  zahlungslink,
  type Buchungsuebersicht,
  type Fakturavermerk,
} from '../api/buchungen';
import { Teilnehmerliste } from './Teilnehmerliste';
import { akte as ladeKundenakte, type Kunde, type Kundenzeile } from '../api/kunden';
import { Buchungssuche } from './Buchungssuche';
import { Buchungsliste } from './Buchungsliste';
import { Kundensuche } from './Kundensuche';
import { Produktsuche } from './Produktsuche';
import { Terminkalenderwahl } from './Terminkalenderwahl';
import { Preisauskunft } from './Preisauskunft';
import {
  Teilnehmererfassung,
  teilnehmerFuerAnlage,
  unlesbareMails,
  type Anmelder,
  type Teilnehmereingabe,
} from './Teilnehmererfassung';
import { liste as ladeArtikel, type Artikelzeile } from '../api/produkte';
import { buchbare, type Terminauswahl as Terminwahl } from '../api/termine';
import {
  bestaetige,
  fuerBuchung as ladeTexte,
  stand as ladeStand,
  stimmeZu,
  verschicke,
  versandvorschau,
  type GeltenderText,
  type Versandvorschau,
  type Zustimmungsstand,
} from '../api/rechtstexte';
import { ApiFehler } from '../api/fehler';
import { alsEuro } from '../betrag';
import { STATUSNAME as AUFTRAGSSTATUSNAME, type Auftragsstatus } from '../api/auftraege';
import { Meldung } from '../komponenten/Meldung';
import { alsTag } from '../datum';
import {
  ERKLAERUNGSWEGNAME,
  Herkunftsfelder,
  Stornokorrektur,
  erklaerungsrumpf,
  type Erklaerungsweg,
} from '../komponenten/Stornoangaben';
import { Rueckfrage } from '../komponenten/Rueckfrage';
import { useTelefon } from '../komponenten/telefon';
import { Telefonfenster } from '../komponenten/Zeilenformular';
import { Abschnitt, Kennzahlenleiste, Reiterleiste } from '../komponenten/Akte';
import { Erfassung } from '../komponenten/Erfassung';
import { Maskenverweis } from '../komponenten/Maskenverweis';
import { Aktenverweis, type Aktenwege } from '../komponenten/Aktenwege';
import type { Kopf } from '../komponenten/Rahmen';
import { BgGutscheine } from '../rechnung/BgZuschuss';
import { BUCHUNGSREITER, type Buchungsfilter, type Buchungsreiter } from '../wegweiser';

/** Die Beschriftung je Reiter der Buchungsakte (Auftrag #36). */
const REITERNAME: Readonly<Record<Buchungsreiter, string>> = {
  uebersicht: 'Übersicht',
  teilnehmer: 'Teilnehmer',
  zusatzleistungen: 'Zusatzleistungen',
  storno: 'Storno und Umbuchung',
  beleg: 'Beleg und Zahlung',
  verlauf: 'Verlauf',
  bedingungen: 'Bedingungen',
};

/**
 * Storno, Umbuchung und Teilnehmerwechsel (BUC-45, BUC-142, BUC-51).
 *
 * ============================================================================
 * DIE ZAHL STEHT DA, BEVOR ETWAS GESCHIEHT
 * ============================================================================
 *
 * BUC-45: „Die Berechnung wird VOR DER AUSFÜHRUNG angezeigt."
 *
 * Diese Maske ist für den Augenblick, in dem jemand am Telefon gefragt wird,
 * was ein Rücktritt kostet. Sie rechnet, sobald die Eingaben stehen, und sie
 * bewegt dabei keinen Platz — die Vorschau geht auf einen eigenen Endpunkt,
 * der nur `buchung:sehen` verlangt. Wer die Auskunft geben soll, muss sie
 * ausrechnen dürfen, ohne stornieren zu dürfen.
 *
 * Der Ausführen-Knopf ist erst da, wenn eine Vorschau vorliegt. Ohne sie
 * wäre die Anzeige nach BUC-45 eine Zusage, die die Oberfläche nicht hält.
 *
 * ============================================================================
 * „NICHT VORGESEHEN" WIRD NICHT ANGEBOTEN
 * ============================================================================
 *
 * FRS-15 verlangt, eine ausgeschlossene Handlung gar nicht erst anzubieten,
 * statt sie anzuzeigen und anschließend abzulehnen. Meldet die Vorschau
 * `nichtVorgesehen`, bleibt der Knopf aus und daneben steht, was stattdessen
 * geht — bei einer Umbuchung: Storno mit Neubuchung.
 *
 * ============================================================================
 * DER ZUGANG DER ERKLÄRUNG IST EIN FELD, KEIN „HEUTE"
 * ============================================================================
 *
 * BUC-144: Ein Rücktritt ist in Textform wirksam. Die Mail vom Freitag
 * 23:50 Uhr, die Montag erfasst wird, fällt in die Stufe des Freitags. Das
 * Feld ist deshalb sichtbar und mit heute vorbelegt — nicht versteckt. An
 * der 42-Tage-Grenze des Firmentrainings ist das der Sprung von 0 auf
 * 15 Prozent.
 */

const HEUTE = (): string => new Date().toISOString().slice(0, 10);

/*
 * CRM-103: Was sofort wirkt und sich schwer umkehren lässt, fragt erst in
 * einem Pop-up nach. Offen ist höchstens eine Rückfrage.
 */
type Nachfrage =
  | { readonly art: 'ausfuehren' }
  | { readonly art: 'zusatz'; readonly leistung: Zusatzleistung }
  | {
      readonly art: 'korrektur';
      readonly vorgang: Vorgangszeile;
      readonly wie: 'herabsetzen' | 'zuruecknehmen';
      readonly betrag: string | null;
      readonly grund: string;
    }
  | { readonly art: 'beleg'; readonly vorgang: Vorgangszeile }
  | { readonly art: 'belegzeilen' }
  | { readonly art: 'versand'; readonly anlass: 'buchung' | 'erneut' }
  | { readonly art: 'bestaetigung'; readonly gegenstand: 'daten_dritter' | 'vorzeitiger_beginn' };

/**
 * Der Zahlungsstand in einem Satz (DAT-11).
 *
 * „angelegt" ist NICHT „bezahlt" — DAT-11 sagt es selbst: „Die Zahlung ist die
 * Geldbewegung, der Zahlungsvorgang die Absicht." Ein Wort, das beides gleich
 * nennt, macht aus einer Absicht eine Einnahme.
 */
/**
 * BUC-43: was der Lauf mit einer Webbuchung getan hat, in Worten.
 *
 * „festgeschrieben" ist das Wort der Buchhaltung; für die Maske steht die
 * Nummer da, denn nach ihr fragt der Kunde.
 */
function fakturatext(faktura: Fakturavermerk): JSX.Element {
  switch (faktura.stand) {
    case 'festgeschrieben':
      return (
        <>
          <span className="marke marke--gut">gestellt</span>{' '}
          {faktura.rechnungsnummer ?? ''}
        </>
      );
    case 'entwurf':
      return <span className="marke marke--warn">Entwurf — Angaben fehlen</span>;
    case 'auftrag':
      return (
        <span className="kanalliste__name">
          später — der Kunde wird nach seiner Kondition berechnet
        </span>
      );
    case 'fehler':
      return (
        <span className="marke marke--warn">
          nicht angelegt ({String(faktura.versuche)}. Versuch)
        </span>
      );
    default:
      return <span className="kanalliste__name">—</span>;
  }
}

function zahlungstext(
  zahlung: {
    readonly status: string;
    readonly betrag: string;
    readonly dienstleister: string | null;
    readonly bestaetigtAm: string | null;
  } | null,
): JSX.Element {
  if (zahlung === null) return <span className="kanalliste__name">kein Vorgang</span>;

  const marke =
    zahlung.status === 'bestaetigt'
      ? 'marke marke--gut'
      : zahlung.status === 'angelegt'
        ? 'marke marke--warn'
        : 'marke marke--inaktiv';
  const wort =
    zahlung.status === 'bestaetigt'
      ? 'bezahlt'
      : zahlung.status === 'angelegt'
        ? 'offen'
        : zahlung.status === 'abgebrochen'
          ? 'abgebrochen'
          : 'fehlgeschlagen';

  return (
    <>
      <span className={marke}>{wort}</span> {alsEuro(zahlung.betrag)}
      {zahlung.dienstleister !== null && (
        <span className="kanalliste__name"> · {zahlung.dienstleister}</span>
      )}
    </>
  );
}


/** Der Zahlungsstand als ein Wort für den Kopf der Akte (DAT-11, Auftrag #36). */
function zahlungswort(status: 'angelegt' | 'bestaetigt' | 'fehlgeschlagen' | 'abgebrochen' | null): string {
  if (status === null) return 'kein Vorgang';
  if (status === 'bestaetigt') return 'bezahlt';
  if (status === 'angelegt') return 'offen';
  return status;
}

function datum(wert: string): string {
  // FRS-01: der Tag in Laatzen, nicht in der Zone des Rechners.
  return alsTag(wert) ?? '—';
}

export function Buchungsvorgang({
  nummer: mitgebracht,
  beiKopf,
  reiter: reiterVonAussen,
  beiReiter,
  beiBuchung,
  filter: filterVonAussen,
  beiListe,
  darfHerabsetzen = false,
  bgGutscheine,
  beiTermin,
  wege,
  anlegen: anlegenVonAussen,
  beiAnlegen,
  meldung: meldungVonAussen,
}: {
  /** CRM-118 (BUC-50): die Wege zu Kunde und Auftrag, nach Recht gesetzt. */
  readonly wege?: Aktenwege;
  /**
   * Eine Buchungsnummer, mit der die Maske aufgeht (B5, 12.09.2026).
   *
   * Die Arbeitsliste unter Service springt mit ihr herein: Sie hat die Frist,
   * nicht die Kennung. Fehlt sie, beginnt die Maske wie bisher mit einem leeren
   * Suchfeld.
   */
  readonly nummer?: string;
  /**
   * Der Aktenkopf, sobald eine Buchung offen ist (ÄA-41). Ohne Buchung bleibt
   * der Kopf der Maske „Buchungen", den die Arbeitsfläche setzt.
   */
  readonly beiKopf?: (kopf: Kopf) => void;
  /**
   * Der Reiter aus der Adresse (Auftrag #36). Mit `beiReiter` führt die
   * Arbeitsfläche ihn — dann ist die Adresse die eine Stelle, an der er steht,
   * und „zurück" öffnet ihn wieder. Ohne beide führt die Maske ihn selbst.
   */
  readonly reiter?: Buchungsreiter;
  readonly beiReiter?: (reiter: Buchungsreiter) => void;
  /**
   * Eine andere Buchung wurde gesucht (Auftrag #36): Die Arbeitsfläche setzt
   * die Adresse, und die Maske lädt sie über `nummer`. Ohne diesen Rückruf
   * lädt sie selbst, wie bisher.
   */
  readonly beiBuchung?: (nummer: string, meldung?: string) => void;
  /**
   * DAS ANLEGEN ALS EIGENE SEITE (Auftrag CRM-175). Mit `beiAnlegen` führt die
   * Arbeitsfläche es — es hat eine eigene Adresse, und „zurück" wie der
   * Menüpunkt „Buchungen" führen aus ihm in die Liste. Ohne beide schaltet die
   * Maske selbst um.
   */
  readonly anlegen?: boolean;
  readonly beiAnlegen?: (offen: boolean) => void;
  /** Eine Rückmeldung, mit der die Maske aufgeht — „angelegt" nach dem Anlegen. */
  readonly meldung?: string;
  /**
   * Die Filter der Liste aus der Adresse (Auftrag CRM-70). Mit `beiListe`
   * führt die Arbeitsfläche sie — ein Filter ersetzt den Eintrag, „Alle
   * Buchungen" legt einen an. Ohne beide führt die Maske sie selbst.
   */
  readonly filter?: Buchungsfilter;
  readonly beiListe?: (filter: Buchungsfilter, ersetzen: boolean) => void;
  /**
   * BUC-146 (AeA-59): die Stornogebühr senken — buchung/herabsetzen, wie die
   * API. Ohne das Recht zeigt die Maske den Knopf nicht.
   */
  readonly darfHerabsetzen?: boolean;
  /** PRD-38 (CRM-157): Gutscheine von Kostenträgern sehen (bgzuschuss/sehen) und erfassen (bgzuschuss/erfassen). */
  readonly bgGutscheine?: { readonly darfErfassen: boolean };
  /** CRM-117 (L6): der Weg in die Terminakte; ohne ihn bleibt der Termin Text. */
  readonly beiTermin?: (termin: string) => void;
} = {}): JSX.Element {
  const [nummer, setzeNummer] = useState(mitgebracht ?? '');
  const [buchung, setzeBuchung] = useState<Buchungsauskunft | null>(null);

  const [eigenerFilter, setzeEigenenFilter] = useState<Buchungsfilter>(filterVonAussen ?? {});
  const filter: Buchungsfilter = beiListe === undefined ? eigenerFilter : (filterVonAussen ?? {});

  /* Auftrag #36: der gewählte Reiter der Akte — von außen geführt oder hier. */
  const [eigenerReiter, setzeEigenerReiter] = useState<Buchungsreiter>(reiterVonAussen ?? 'uebersicht');
  const reiter: Buchungsreiter = beiReiter === undefined ? eigenerReiter : (reiterVonAussen ?? 'uebersicht');
  const waehleReiter = (r: Buchungsreiter): void => {
    setzeEigenerReiter(r);
    beiReiter?.(r);
  };

  /*
   * BUC-43: der Vermerk der automatischen Rechnung. `?? null`, weil eine
   * Antwort von vor dem 19.09.2026 das Feld nicht kennt — sie soll die Maske
   * nicht zum Absturz bringen.
   */
  const faktura: Fakturavermerk | null = buchung?.faktura ?? null;
  const [historie, setzeHistorie] = useState<readonly Vorgangszeile[]>([]);
  const [fehler, setzeFehler] = useState<ApiFehler | null>(null);
  const [laeuft, setzeLaeuft] = useState(false);

  const [art, setzeArt] = useState<Vorgangsart>('storno');
  const [zugangAm, setzeZugangAm] = useState(HEUTE);
  /* BUC-144 (AeA-59): wie die Erklärung einging; nur beim Storno. */
  const [herkunft, setzeHerkunft] = useState<{ readonly weg: Erklaerungsweg | ''; readonly verweis: string }>({
    weg: '',
    verweis: '',
  });
  /* BUC-146, BUC-148: das offene Formular zum Herabsetzen oder Zurücknehmen. */
  const [korrektur, setzeKorrektur] = useState<
    { readonly vorgang: string; readonly art: 'herabsetzen' | 'zuruecknehmen' } | null
  >(null);
  const [nachfrage, setzeNachfrage] = useState<Nachfrage | null>(null);
  const [plaetze, setzePlaetze] = useState<string>('');
  const [grundbetrag, setzeGrundbetrag] = useState('');
  const [zielTermin, setzeZielTermin] = useState('');
  const [grund, setzeGrund] = useState('');
  /*
   * C3.4, C3.7: wer beim Teilstorno geht oder bei der Teilumbuchung umzieht.
   * Leer heisst: Die Regel des Servers gilt -- zuerst die Plaetze ohne Namen.
   */
  const [auswahl, setzeAuswahl] = useState<readonly string[]>([]);

  const [vorschau, setzeVorschau] = useState<Vorschau | null>(null);
  const [erledigt, setzeErledigt] = useState<string | null>(null);
  /* Kurze Rueckmeldungen zu Buchung und Teilnehmermeldung. */
  const [meldung, setzeMeldung] = useState<string | null>(meldungVonAussen ?? null);
  const [ziele, setzeZiele] = useState<readonly Terminauswahl[]>([]);
  const [teilnehmer, setzeTeilnehmer] = useState<readonly Teilnehmerzeile[]>([]);
  const [belegmeldung, setzeBelegmeldung] = useState<readonly string[] | null>(null);

  /* BUC-69/BUC-70: die Teilnehmer dieser Buchung und die Arbeitsliste. */
  const [eigeneTeilnehmer, setzeEigeneTeilnehmer] = useState<readonly Teilnehmerstand[]>([]);
  const [namen, setzeNamen] = useState<Record<number, string>>({});

  /* BUC-50/BUC-51: eine neue Buchung — von außen geführt oder hier (CRM-175). */
  const [eigenesAnlegen, setzeEigenesAnlegen] = useState(false);
  const neuOffen = beiAnlegen === undefined ? eigenesAnlegen : anlegenVonAussen === true;
  const setzeNeuOffen = useCallback(
    (offen: boolean): void => {
      setzeEigenesAnlegen(offen);
      beiAnlegen?.(offen);
    },
    [beiAnlegen],
  );
  const [nTermin, setzeNTermin] = useState('');
  const [nKunde, setzeNKunde] = useState('');
  /*
   * DER WEG ZUM TERMIN GEHT ÜBER DAS PRODUKT.
   *
   * Ein Terminfeld, das alle Termine des Hauses anböte, wäre nach dem ersten
   * Betriebsjahr eine Liste mit tausend Einträgen. Wer bucht, weiß, WAS
   * gebucht wird — das Produkt ist die natürliche erste Frage, und danach
   * sind es eine Handvoll Termine.
   */
  const [nArtikel, setzeNArtikel] = useState('');
  const [artikel, setzeArtikel] = useState<readonly Artikelzeile[]>([]);
  const [terminwahl, setzeTerminwahl] = useState<readonly Terminwahl[]>([]);
  /*
   * BUC-80 (19.09.2026): Kunde, Produkt und Termin werden in eigenen Fenstern
   * gewählt — Suchmaske, Suchmaske, Kalender. Offen ist höchstens eines.
   * CRM-94: Der Zieltermin der Umbuchung kommt aus demselben Kalender.
   */
  const [fenster, setzeFenster] = useState<
    'buchung' | 'kunde' | 'produkt' | 'termin' | 'zieltermin' | null
  >(null);
  const [gewaehlterKunde, setzeGewaehltenKunden] = useState<Kundenzeile | undefined>(undefined);
  /* BUC-127: die Akte des Kunden — sie nennt, wer anmeldet. */
  const [kundenakte, setzeKundenakte] = useState<Kunde | null>(null);
  const [nBesteller, setzeNBesteller] = useState('');
  const [nAnmelderDabei, setzeNAnmelderDabei] = useState(false);
  const [nTeilnehmer, setzeNTeilnehmer] = useState<Record<number, Teilnehmereingabe>>({});
  /* BUC-80: Was die Buchung kostet — vor dem Buchen. */
  const [preis, setzePreis] = useState<Preisangabe | null>(null);
  const [preisLaeuft, setzePreisLaeuft] = useState(false);
  const [nPlaetze, setzeNPlaetze] = useState('1');
  const [nNummer, setzeNNummer] = useState('');
  const [nWeg, setzeNWeg] = useState<'portal' | 'innendienst' | 'angebot'>('innendienst');
  const [nNachgebucht, setzeNNachgebucht] = useState(false);
  const [nFrist, setzeNFrist] = useState('');
  const [nBestellreferenz, setzeNBestellreferenz] = useState('');
  /* BUC-25, BUC-123: was diese Buchung noch schuldet. */
  const [rechtsstand, setzeRechtsstand] = useState<Zustimmungsstand | null>(null);
  const [rechtstexte, setzeRechtstexte] = useState<readonly GeltenderText[]>([]);
  const [versandbild, setzeVersandbild] = useState<Versandvorschau | null>(null);
  const [versandmeldung, setzeVersandmeldung] = useState<string | null>(null);
  /* BUC-119: die gewählten Zusatzleistungen und die Auswahl dafür. */
  const [zusatz, setzeZusatz] = useState<readonly Zusatzleistung[]>([]);
  /* BUC-84, BUC-85 i. d. F. ÄA-31: `frei` ist `null`, wo kein Pool dahintersteht. */
  /* AeA-31, Nachtrag: nach welcher Liste die Zusatzleistungen rechnen. */
  const [zusatzpreisliste, setzeZusatzpreisliste] = useState<Zusatzpreisliste>('standard');
  const [zusatzangebot, setzeZusatzangebot] = useState<
    readonly { readonly id: string; readonly bezeichnung: string; readonly frei: number | null }[]
  >([]);
  const [zArtikel, setzeZArtikel] = useState('');
  /* CRM-106: Auf dem Telefon steht die Wahl einer Zusatzleistung im Dialog. */
  const telefon = useTelefon();
  const [zFensterOffen, setzeZFensterOffen] = useState(false);
  const [zTeilnehmer, setzeZTeilnehmer] = useState('');
  const [uebersicht, setzeUebersicht] = useState<Buchungsuebersicht | null>(null);
  const [zPruefung, setzeZPruefung] = useState<{
    readonly einwaende: readonly Einwand[];
    readonly verdraengt: readonly Verdraengt[];
  } | null>(null);
  const [nKostenstelle, setzeNKostenstelle] = useState('');

  /**
   * BUC-121: Belegpositionen erzeugen — und die Akte danach neu laden.
   *
   * Ohne das Nachladen stünde „noch keine" unter einem Knopf, der gerade
   * welche gemacht hat.
   */
  /*
   * CRM-103: Die schreibenden Aufrufe werfen, damit die Rückfrage ihren
   * Fehler zeigt und offen bleibt. Was danach nur nachlädt, geht hier durch:
   * Ein Fehler beim Nachladen gehört auf die Seite — die Tat ist geschehen,
   * und ein zweiter Klick im Pop-up schriebe sie noch einmal.
   */
  const aufDieSeite = useCallback(async (was: () => Promise<void>) => {
    try {
      await was();
    } catch (f) {
      setzeFehler(f instanceof ApiFehler ? f : null);
      if (!(f instanceof ApiFehler)) throw f;
    }
  }, []);

  const belegzeilenErzeugen = useCallback(async () => {
    if (buchung === null) return;
    setzeLaeuft(true);
    setzeFehler(null);
    setzeMeldung(null);
    try {
      const ergebnis = await belegpositionenErzeugen(buchung.id);
      setzeNachfrage(null);
      setzeMeldung(
        `${String(ergebnis.positionen)} Belegposition${ergebnis.positionen === 1 ? '' : 'en'} `
        + 'am Auftrag angelegt — je Teilnehmer eine.',
      );
      await aufDieSeite(async () => {
        setzeBuchung(await ueberNummer(buchung.buchungsnummer));
      });
    } finally {
      setzeLaeuft(false);
    }
  }, [buchung, aufDieSeite]);

  /**
   * WEB-12, BUC-37: den Zahlungsvorgang beim Dienstleister erzeugen.
   *
   * DER BETRAG KOMMT AUS DER ÜBERSICHT und wird nicht getippt: BUC-37 sagt
   * „Beträge und Referenzen werden NIE vom Portal bestimmt" — und ein Feld,
   * in das jemand einen Betrag tippt, ist dieselbe Lücke mit anderem
   * Vorzeichen. Es ist der OFFENE Betrag, wie der Server ihn rechnet
   * (RNG-27, AP-FA-03): Nach einer Anzahlung forderte die Gesamtsumme doppelt.
   *
   * DIE WEITERLEITUNG WIRD GEZEIGT UND NICHT GEÖFFNET. Sie geht an den
   * KUNDEN; der Innendienst schickt sie ihm. Dieses Fenster automatisch
   * dorthin zu schicken hieße, den Mitarbeiter auf die Bezahlseite seines
   * Kunden zu setzen.
   */
  const linkErzeugen = useCallback(async () => {
    if (buchung === null || uebersicht === null || nichtsOffen(uebersicht)) return;
    setzeLaeuft(true);
    setzeFehler(null);
    setzeMeldung(null);
    try {
      const ergebnis = await zahlungslink({
        buchung: buchung.id,
        buchungsnummer: buchung.buchungsnummer,
        offen: uebersicht.offen,
        beschreibung: `${buchung.artikel}, ${String(buchung.plaetze)} Plätze`,
      });
      setzeMeldung(
        (ergebnis.wiederholt
          ? 'Zu diesem Betrag besteht bereits ein Vorgang; es wurde kein zweiter angelegt. '
          : 'Zahlungsvorgang angelegt. ')
        + `Link für den Kunden: ${ergebnis.weiterleitung}`,
      );
      setzeBuchung(await ueberNummer(buchung.buchungsnummer));
    } catch (f) {
      setzeFehler(f instanceof ApiFehler ? f : null);
      if (!(f instanceof ApiFehler)) throw f;
    } finally {
      setzeLaeuft(false);
    }
  }, [buchung, uebersicht]);

  const oeffne = useCallback(
    async (gesucht: string) => {
      if (gesucht.trim() === '') return;
      setzeLaeuft(true);
      setzeFehler(null);
      setzeVorschau(null);
      setzeErledigt(null);
      try {
        const gefunden = await ueberNummer(gesucht.trim());
        setzeBuchung(gefunden);
        setzePlaetze(String(gefunden.plaetze));
        setzeHistorie(await ladeVorgaenge(gefunden.id));
        setzeTeilnehmer(await ladeTeilnehmer(gefunden.termin));
        setzeEigeneTeilnehmer(await teilnehmerZurBuchung(gefunden.id));
        setzeNamen({});
      } catch (f) {
        setzeBuchung(null);
        setzeHistorie([]);
        setzeTeilnehmer([]);
        setzeFehler(f instanceof ApiFehler ? f : null);
        if (!(f instanceof ApiFehler)) throw f;
      } finally {
        setzeLaeuft(false);
      }
    },
    [],
  );

  /*
   * EINE BUCHUNG ÖFFNEN (Auftrag #36): Führt die Arbeitsfläche die Adresse,
   * geht der Weg über sie — die Nummer steht dann in der Zeile, und „zurück"
   * führt zur vorigen Buchung. Dieselbe Nummer noch einmal lädt direkt neu.
   */
  const waehleBuchung = useCallback(
    async (gesucht: string) => {
      const n = gesucht.trim();
      if (beiBuchung !== undefined && n !== '' && n !== (mitgebracht ?? '').trim()) {
        beiBuchung(n);
        return;
      }
      await oeffne(gesucht);
    },
    [beiBuchung, mitgebracht, oeffne],
  );

  const suche = useCallback(
    async (ereignis?: FormEvent) => {
      ereignis?.preventDefault();
      await waehleBuchung(nummer);
    },
    [nummer, waehleBuchung],
  );

  /*
   * EINE MITGEBRACHTE NUMMER WIRD GESUCHT (B5, 12.09.2026).
   *
   * Wer aus der Arbeitsliste hereinkommt, hat die Buchung schon gewaehlt — noch
   * einmal „Suchen" zu druecken waere ein Handgriff, der nichts entscheidet.
   *
   * EINMAL UND NICHT BEI JEDER AENDERUNG: Die Abhaengigkeit ist die MITGEBRACHTE
   * Nummer und nicht `nummer`. Sonst suchte die Maske bei jedem getippten
   * Zeichen im Suchfeld erneut.
   */
  useEffect(() => {
    if (mitgebracht !== undefined && mitgebracht.trim() !== '') {
      setzeNummer(mitgebracht);
      void oeffne(mitgebracht);
    } else if (beiBuchung !== undefined) {
      /* Auftrag #36: „zurück" auf `/buchungen` — die Adresse nennt keine Buchung mehr. */
      setzeBuchung(null);
    }
    /*
     * Nur `mitgebracht`: Das Suchfeld (`nummer`) aendert sich beim Tippen, und
     * die Maske soll nicht bei jedem Zeichen suchen. Gewollt ist ein Lauf, wenn
     * eine Nummer mitgebracht wird — aus einem Sprung oder aus der Adresse.
     */
  }, [mitgebracht]);

  /*
   * DIE VORSCHAU LÄUFT BEI JEDER ÄNDERUNG MIT.
   *
   * Ein eigener „Berechnen"-Knopf wäre ein Zwischenschritt, den jemand
   * vergisst — und dann steht neben dem Ausführen-Knopf eine Zahl von vorhin.
   * Sie hier an die Eingaben zu hängen macht das unmöglich: Ändert sich der
   * Zugang, ist die alte Vorschau im selben Augenblick weg.
   */
  useEffect(() => {
    if (buchung === null) return;
    let abgebrochen = false;
    setzeVorschau(null);
    const anzahl = Number(plaetze);
    void (async () => {
      try {
        const gerechnet = await ladeVorschau(buchung.id, art, {
          zugangAm,
          ...(grundbetrag.trim() === '' ? {} : { grundbetrag: grundbetrag.trim() }),
          ...(Number.isInteger(anzahl) && anzahl > 0 ? { plaetze: anzahl } : {}),
          ...(teilweise(buchung.plaetze, anzahl) && auswahl.length === anzahl ? { teilnehmer: auswahl } : {}),
        });
        if (!abgebrochen) {
          setzeVorschau(gerechnet);
          setzeFehler(null);
        }
      } catch (f) {
        if (!abgebrochen) setzeFehler(f instanceof ApiFehler ? f : null);
      }
    })();
    return () => {
      abgebrochen = true;
    };
  }, [buchung, art, zugangAm, plaetze, grundbetrag, auswahl]);

  /*
   * DIE ZIELTERMINE KOMMEN VOM SERVER, MIT IHRER FREIEN KAPAZITÄT.
   *
   * Vorher stand hier ein Textfeld für eine Kennung — für den Innendienst
   * unbenutzbar, und für eine Umbuchung in einen vollen Termin genau die
   * Gelegenheit, die FRS-15 ausschließen will. Die Liste zeigt nur, was
   * buchbar ist, und der Ausgangstermin steht nicht darin.
   *
   * `frei` ist die MASSGEBLICHE Kapazität (KAP-03), nicht
   * „Höchstteilnehmer minus Belegung": Ein Termin mit zwölf Plätzen und einer
   * Ressourcengrenze von sechs hat sechs.
   */
  useEffect(() => {
    if (buchung === null || art !== 'umbuchung') {
      setzeZiele([]);
      return;
    }
    let abgebrochen = false;
    void (async () => {
      try {
        const gefunden = await buchbareTermine(buchung.termin, buchung.termin);
        if (!abgebrochen) setzeZiele(gefunden);
      } catch (f) {
        if (!abgebrochen) setzeFehler(f instanceof ApiFehler ? f : null);
      }
    })();
    return () => {
      abgebrochen = true;
    };
  }, [buchung, art]);

  const ausfuehren = useCallback(async () => {
    if (buchung === null || vorschau === null) return;
    const anzahl = Number(plaetze);
    setzeLaeuft(true);
    setzeFehler(null);
    try {
      const ergebnis = await fuehreAus(buchung.id, art, {
        zugangAm,
        ...(grundbetrag.trim() === '' ? {} : { grundbetrag: grundbetrag.trim() }),
        ...(Number.isInteger(anzahl) && anzahl > 0 ? { plaetze: anzahl } : {}),
        ...(art === 'umbuchung' ? { zielTermin: zielTermin.trim() } : {}),
        ...(grund.trim() === '' ? {} : { grund: grund.trim() }),
        ...(art !== 'teilnehmerwechsel' && teilweise(buchung.plaetze, anzahl) && auswahl.length > 0
          ? { teilnehmer: auswahl }
          : {}),
        ...(art === 'storno'
          ? erklaerungsrumpf({ zugang: zugangAm, weg: herkunft.weg, verweis: herkunft.verweis })
          : {}),
      });
      setzeNachfrage(null);
      setzeErledigt(ergebnis.vorgang);
      setzeAuswahl([]);
      await aufDieSeite(async () => {
        const frisch = await ueberNummer(buchung.buchungsnummer);
        setzeBuchung(frisch);
        setzeHistorie(await ladeVorgaenge(frisch.id));
        setzeTeilnehmer(await ladeTeilnehmer(frisch.termin));
        setzeEigeneTeilnehmer(await teilnehmerZurBuchung(frisch.id));
      });
    } finally {
      setzeLaeuft(false);
    }
  }, [buchung, vorschau, art, zugangAm, plaetze, grundbetrag, zielTermin, grund, auswahl, herkunft, aufDieSeite]);

  /**
   * Den Beleg zu einem Vorgang erzeugen (BUC-145).
   *
   * Er entsteht als ENTWURF. Die Hinweise werden ANGEZEIGT und nicht
   * verschluckt: Steht die Verrechnung nicht auf dem Beleg, weil der
   * Steuerschlüssel der Anzahlung nicht feststeht, muss der Bearbeiter das
   * erfahren, bevor er festschreibt (§ 14c UStG).
   */
  /*
   * DIE RECHTSTEXTE ZUR GEFUNDENEN BUCHUNG (BUC-25, BUC-123).
   *
   * Erst nach der Suche, weil sie an der Buchung hängen: Welche Fassungen
   * gelten, entscheiden der Artikel des Termins und die EINGEFRORENE
   * Kundenart — nicht die des Kunden von heute (BUC-106).
   */
  useEffect(() => {
    if (buchung === null) {
      setzeRechtsstand(null);
      setzeRechtstexte([]);
      return;
    }
    let abgebrochen = false;
    setzeVersandmeldung(null);
    void Promise.all([
      ladeStand(buchung.id),
      ladeTexte(buchung.id),
      versandvorschau(buchung.id),
    ])
      .then(([s, x, v]) => {
        if (abgebrochen) return;
        setzeRechtsstand(s);
        setzeRechtstexte(x);
        setzeVersandbild(v);
      })
      .catch(() => {
        if (!abgebrochen) {
          setzeRechtsstand(null);
          setzeRechtstexte([]);
          setzeVersandbild(null);
        }
      });
    return () => {
      abgebrochen = true;
    };
  }, [buchung]);

  const zustimmen = useCallback(
    async (rechtstext: string) => {
      if (buchung === null) return;
      try {
        const antwort = await stimmeZu(buchung.id, rechtstext);
        setzeRechtsstand(antwort.stand);
      } catch (f) {
        setzeFehler(f instanceof ApiFehler ? f : null);
      }
    },
    [buchung],
  );

  // CRM-103: wirft — der Fehler steht in der Rückfrage.
  const bestaetigen = useCallback(
    async (gegenstand: 'daten_dritter' | 'vorzeitiger_beginn') => {
      if (buchung === null) return;
      const antwort = await bestaetige(buchung.id, gegenstand);
      setzeNachfrage(null);
      setzeRechtsstand(antwort.stand);
    },
    [buchung],
  );

  /*
   * DAS ANGEBOT AN ZUSATZLEISTUNGEN (BUC-84 i. d. F. ÄA-31).
   *
   * Trägt das Produkt ein Angebot, steht nur das zur Wahl — mit der Zahl, die
   * zum Termin noch frei ist (BUC-85). Trägt es keins, alles mit der
   * Vertriebsform „Zusatzleistung" (PRD-41); was keine ist, lässt sich auch
   * nicht als eine buchen, und die Datenbank sagte es sonst erst nach dem
   * Klick.
   */
  const angebotLaden = useCallback(async (id: string) => {
    try {
      const stand = await zusatzangebotZurBuchung(id);
      setzeZusatzpreisliste(stand.zusatzpreisliste);
      if (stand.beschraenkt) {
        setzeZusatzangebot(
          stand.angebote.map((a) => ({ id: a.zusatz, bezeichnung: a.bezeichnung, frei: a.frei })),
        );
        return;
      }
      const g = await ladeArtikel({ nurWaehlbar: true, vertriebsform: 'zusatzleistung' });
      setzeZusatzangebot(g.artikel.map((a) => ({ id: a.id, bezeichnung: a.bezeichnung, frei: null })));
    } catch {
      setzeZusatzangebot([]);
    }
  }, []);

  /*
   * DIE UEBERSICHT WIRD MIT DEN LEISTUNGEN GEHOLT (BUC-122).
   *
   * Sie hängt an ihnen: Wer eine Zusatzleistung wählt oder storniert, ändert
   * die Summe. Getrennt geladen zeigte die Maske nach jedem Klick eine Zahl
   * von vorhin — und das ist die gefährlichste Anzeige einer Preisübersicht.
   */
  const zusatzLaden = useCallback(async (id: string) => {
    void angebotLaden(id);
    try {
      const [leistungen, u] = await Promise.all([ladeZusatz(id), ladeUebersicht(id)]);
      setzeZusatz(leistungen);
      setzeUebersicht(u);
    } catch {
      setzeZusatz([]);
      setzeUebersicht(null);
    }
  }, [angebotLaden]);

  useEffect(() => {
    if (buchung === null) {
      setzeZusatz([]);
      setzeZusatzangebot([]);
      return;
    }
    void zusatzLaden(buchung.id);
  }, [buchung, zusatzLaden]);

  /*
   * VOR DER WAHL WIRD GEFRAGT (BUC-86, FRS-15).
   *
   * Was abgewiesen würde, wird gar nicht erst angeboten; was verdrängt wird,
   * steht vorher da. Eine Wahl, deren Folgen man erst hinterher erfährt,
   * lässt sich nicht zurücknehmen, ohne dass eine zweite Zeile im Protokoll
   * steht.
   */
  useEffect(() => {
    if (buchung === null || zArtikel === '') {
      setzeZPruefung(null);
      return;
    }
    let abgebrochen = false;
    void zusatzPruefen(buchung.id, zArtikel, zTeilnehmer === '' ? null : zTeilnehmer)
      .then((p) => {
        if (!abgebrochen) setzeZPruefung(p);
      })
      .catch(() => {
        if (!abgebrochen) setzeZPruefung(null);
      });
    return () => {
      abgebrochen = true;
    };
  }, [buchung, zArtikel, zTeilnehmer]);

  /** `true`, wenn die Leistung gewählt ist — dann schließt der Dialog auf dem Telefon. */
  const zusatzWaehlen = useCallback(async (): Promise<boolean> => {
    if (buchung === null || zArtikel === '') return false;
    setzeFehler(null);
    try {
      await zusatzWaehle(buchung.id, {
        artikel: zArtikel,
        teilnehmer: zTeilnehmer === '' ? null : zTeilnehmer,
        /* PRD-26: Was zu einer BESTEHENDEN Buchung dazukommt, ist nachgebucht
         * — und wirkt auf Tagesplan und Beleg. */
        nachgebucht: true,
      });
      setzeZArtikel('');
      setzeZTeilnehmer('');
      await zusatzLaden(buchung.id);
      return true;
    } catch (f) {
      setzeFehler(f instanceof ApiFehler ? f : null);
      return false;
    }
  }, [buchung, zArtikel, zTeilnehmer, zusatzLaden]);

  /*
   * STANDARD ODER MITGLIED (AeA-31, Nachtrag).
   *
   * Bis BUC-91 NOCH-OFFEN die Mitgliedschaft prüft, wählt der Innendienst. Schon
   * gewählte Leistungen rechnen danach neu — die Übersicht wird deshalb mit
   * den Leistungen zusammen neu geholt.
   */
  const preislisteWaehlen = useCallback(
    async (liste: Zusatzpreisliste) => {
      if (buchung === null) return;
      setzeFehler(null);
      try {
        await zusatzpreislisteSetzen(buchung.id, liste);
        await zusatzLaden(buchung.id);
      } catch (f) {
        setzeFehler(f instanceof ApiFehler ? f : null);
      }
    },
    [buchung, zusatzLaden],
  );

  // CRM-103: wirft — der Fehler steht in der Rückfrage.
  const zusatzStornieren = useCallback(
    async (leistung: string) => {
      if (buchung === null) return;
      setzeFehler(null);
      await zusatzStorniere(leistung);
      setzeNachfrage(null);
      await zusatzLaden(buchung.id);
    },
    [buchung, zusatzLaden],
  );

  // CRM-103: wirft — der Fehler steht in der Rückfrage.
  const versenden = useCallback(
    async (anlass: 'buchung' | 'erneut') => {
      if (buchung === null) return;
      setzeVersandmeldung(null);
      const e = await verschicke(buchung.id, anlass);
      setzeNachfrage(null);
      setzeVersandmeldung(
        `An ${e.empfaenger.join(', ')} verschickt (${String(e.nachrichten)} Nachricht`
        + `${e.nachrichten === 1 ? '' : 'en'}).`
        + (e.probe
          ? ' Als Probe verschickt — zählt nicht als Übermittlung und muss wiederholt'
            + ' werden.'
          : '')
        + (e.rueckfallAufAnmelder
          ? ' Die Teilnehmerunterlagen gingen an den Anmelder — für die Teilnehmer ist'
            + ' keine Adresse hinterlegt.'
          : ''),
      );
    },
    [buchung],
  );

  /*
   * BUC-101: Die Kundenart bestimmt die Pflichtfelder. Sie steht am gewählten
   * Kunden — deshalb wird er hier gesucht und nicht die Art daneben geführt.
   */
  /*
   * BUC-127: WER ANMELDET. Beim Privatkunden die Person selbst, beim
   * Geschäftskunden der Ansprechpartner, der die Buchung aufgibt — er ist
   * der Besteller (BUC-103). Name, E-Mail und Geburtsdatum kommen aus der
   * Akte; getippt wird nichts ein zweites Mal.
   */
  const anmelder: Anmelder | null = useMemo<Anmelder | null>(() => {
    if (kundenakte === null || kundenakte.id !== nKunde) return null;
    const primaereMail = (kanaele: Kunde['kanaele']): string | null =>
      (kanaele.find((k) => k.art === 'email' && k.istPrimaer) ?? kanaele.find((k) => k.art === 'email'))
        ?.wert ?? null;
    if (kundenakte.kundenart === 'privat') {
      if (kundenakte.person === null) return null;
      return {
        name: [kundenakte.person.vorname, kundenakte.person.nachname].filter((t) => t !== null).join(' '),
        email: primaereMail(kundenakte.kanaele),
        geburtsdatum: kundenakte.person.geburtsdatum,
      };
    }
    const kontakt = kundenakte.kontakte.find((k) => k.id === nBesteller);
    if (kontakt === undefined) return null;
    return {
      name: [kontakt.person.vorname, kontakt.person.nachname].filter((t) => t !== null).join(' '),
      email: primaereMail(kontakt.kanaele),
      geburtsdatum: kontakt.person.geburtsdatum,
    };
  }, [kundenakte, nKunde, nBesteller]);

  /*
   * DIE AKTE KOMMT MIT DER WAHL DES KUNDEN. Scheitert sie, bleibt die Buchung
   * möglich — nur der Haken „Anmelder ist Teilnehmer" steht dann nicht zur
   * Wahl.
   */
  useEffect(() => {
    setzeKundenakte(null);
    setzeNBesteller('');
    if (nKunde === '') return;
    let abgebrochen = false;
    ladeKundenakte(nKunde)
      .then((a) => {
        if (abgebrochen) return;
        setzeKundenakte(a);
        /* Der Privatkunde bucht meist für sich selbst — der Haken ist vorbelegt. */
        setzeNAnmelderDabei(a.kundenart === 'privat');
      })
      .catch(() => {
        if (!abgebrochen) setzeKundenakte(null);
      });
    return () => {
      abgebrochen = true;
    };
  }, [nKunde]);

  /*
   * DIE PRODUKTLISTE HÄNGT AM KUNDEN (BUC-105).
   *
   * Ohne gewählten Kunden bleibt sie leer — nicht weil nichts da wäre,
   * sondern weil noch nicht feststeht, was angeboten werden darf. Wechselt
   * der Kunde die Art, wird sie neu geholt: Was dem einen offenstand, muss
   * dem anderen nicht offenstehen.
   */
  useEffect(() => {
    if (!neuOffen || gewaehlterKunde === undefined) {
      setzeArtikel([]);
      setzeNArtikel('');
      return;
    }
    let abgebrochen = false;
    ladeArtikel({ nurWaehlbar: true, fuerKundenart: gewaehlterKunde.kundenart })
      .then((g) => {
        if (!abgebrochen) setzeArtikel(g.artikel);
      })
      .catch(() => {
        if (!abgebrochen) setzeArtikel([]);
      });
    return () => {
      abgebrochen = true;
    };
  }, [neuOffen, gewaehlterKunde]);

  useEffect(() => {
    if (nArtikel === '') {
      setzeTerminwahl([]);
      setzeNTermin('');
      return;
    }
    let abgebrochen = false;
    /*
     * MIT DEM ANFRAGENDEN (KAP-04) UND SEINER KUNDENART (BUC-105).
     *
     * Beides steht fest, bevor das Produkt gewählt wird — die Maske fragt in
     * dieser Reihenfolge, damit die freie Kapazität für DIESEN Kunden gilt
     * und nicht die anonyme Sicht zeigt.
     */
    buchbare(nArtikel, undefined, nKunde, gewaehlterKunde?.kundenart)
      .then((g) => {
        if (!abgebrochen) setzeTerminwahl(g);
      })
      .catch(() => {
        if (!abgebrochen) setzeTerminwahl([]);
      });
    return () => {
      abgebrochen = true;
    };
  }, [nArtikel, nKunde, gewaehlterKunde]);


  /*
   * DER PREIS WIRD GEHOLT, SOBALD DIE FRAGE VOLLSTAENDIG IST (BUC-80).
   *
   * Termin, Kunde und Platzzahl — vorher gibt es keine Antwort, nur eine
   * halbe Frage. Getaktet, weil die Platzzahl getippt wird: Sonst liefe je
   * Zeichen eine Preisfindung.
   */
  useEffect(() => {
    const anzahl = Number(nPlaetze);
    if (!neuOffen || nTermin === '' || nKunde === '' || !Number.isInteger(anzahl) || anzahl < 1) {
      setzePreis(null);
      setzePreisLaeuft(false);
      return;
    }
    let abgebrochen = false;
    setzePreisLaeuft(true);
    const uhr = setTimeout(() => {
      ladePreis({ termin: nTermin, kunde: nKunde, plaetze: anzahl })
        .then((a) => {
          if (!abgebrochen) setzePreis(a);
        })
        .catch(() => {
          /*
           * Eine gescheiterte Auskunft sperrt das Buchen NICHT. Sie ist eine
           * Auskunft; der Preis entsteht am Auftrag, nicht hier.
           */
          if (!abgebrochen) setzePreis(null);
        })
        .finally(() => {
          if (!abgebrochen) setzePreisLaeuft(false);
        });
    }, 300);
    return () => {
      abgebrochen = true;
      clearTimeout(uhr);
    };
  }, [neuOffen, nTermin, nKunde, nPlaetze]);

  /**
   * Teilnehmernamen nachreichen (BUC-69).
   *
   * Eine abgelaufene Frist SPERRT NICHT — BUC-70: „der Termin bleibt
   * trotzdem planbar." Sie ist eine Mahnungsgrenze; das Gegenteil hieße, dass
   * ein Teilnehmer nicht teilnehmen darf, weil sein Name zu spät kam.
   */
  const melden = useCallback(async () => {
    if (buchung === null) return;
    const eintraege = Object.entries(namen)
      .filter(([, n]) => n.trim() !== '')
      .map(([position, n]) => ({ position: Number(position), name: n.trim() }));
    if (eintraege.length === 0) return;

    setzeLaeuft(true);
    setzeFehler(null);
    try {
      const ergebnis = await teilnehmerMelden(buchung.id, eintraege);
      setzeMeldung(
        ergebnis.nachFristablauf
          ? `${String(ergebnis.gemeldet)} Name(n) übernommen — nach Ablauf der Meldefrist.`
          : `${String(ergebnis.gemeldet)} Name(n) übernommen.`,
      );
      setzeEigeneTeilnehmer(await teilnehmerZurBuchung(buchung.id));
      setzeNamen({});
      const frisch = await ueberNummer(buchung.buchungsnummer);
      setzeBuchung(frisch);
    } catch (f) {
      setzeFehler(f instanceof ApiFehler ? f : null);
      if (!(f instanceof ApiFehler)) throw f;
    } finally {
      setzeLaeuft(false);
    }
  }, [buchung, namen]);

  const buchen = useCallback(
    async () => {
      setzeLaeuft(true);
      setzeFehler(null);
      setzeMeldung(null);
      /* BUC-80, BUC-127: die schon bekannten Namen gehen mit der Buchung. */
      const eingabeTeilnehmer = teilnehmerFuerAnlage(
        Number(nPlaetze),
        anmelder,
        nAnmelderDabei,
        nTeilnehmer,
      );
      try {
        const antwort = await anlegen({
          termin: nTermin.trim(),
          kunde: nKunde.trim(),
          plaetze: Number(nPlaetze),
          /*
            B6: LEER HEISST „VERGIB EINE" (BUC-50, BP-16).

            Wer aus dem Portal eine Nummer mitbringt, traegt sie ein. Wer am
            Telefon nachbucht, laesst das Feld leer — der Nummernkreis zieht die
            naechste beim Schreiben. Ein Vorschlag beim Oeffnen waere bequemer und
            falsch: Jede abgebrochene Eingabe waere eine Nummer, die vergeben ist
            und nie belegt wird, und die muesste nach BP-16 erklaert werden.
          */
          ...(nNummer.trim() === '' ? {} : { buchungsnummer: nNummer.trim() }),
          weg: nWeg,
          nachgebucht: nNachgebucht,
          ...(nFrist === '' ? {} : { teilnehmerFrist: new Date(nFrist).toISOString() }),
          ...(nBestellreferenz.trim() === ''
            ? {}
            : { bestellreferenz: nBestellreferenz.trim() }),
          ...(nKostenstelle.trim() === '' ? {} : { kostenstelle: nKostenstelle.trim() }),
          /* BUC-103, BUC-127: der Ansprechpartner, der anmeldet. */
          ...(nBesteller === '' || gewaehlterKunde?.kundenart === 'privat'
            ? {}
            : { besteller: nBesteller }),
          ...(eingabeTeilnehmer.length === 0 ? {} : { teilnehmer: eingabeTeilnehmer }),
        });
        const angelegt =
          (nNummer.trim() === ''
            ? 'Die Buchung ist angelegt; die Buchungsnummer wurde aus dem Nummernkreis vergeben.'
            : `Die Buchung ${nNummer.trim()} ist angelegt.`)
          /*
           * BUC-121: Mit der Buchung entsteht ihr Auftrag — er trägt den Preis
           * und ist die Belegseite. Ihn zu nennen ist keine Zierde: Wer die
           * Rechnung sucht, sucht sie über die Auftragsnummer.
           */
          + (antwort.auftragsnummer === undefined
            ? ''
            : ` Auftrag ${antwort.auftragsnummer} angelegt.`)
          + (eingabeTeilnehmer.length === 0
            ? ''
            : ` ${String(eingabeTeilnehmer.length)} Teilnehmer erfasst.`);
        setzeNTeilnehmer({});
        /*
          DANACH STEHT DIE NEUE BUCHUNG OFFEN (BUC-50, CRM-175).

          Die vergebene Nummer kennt nur der Server; seit CRM-175 nennt er sie in
          der Antwort. Führt die Arbeitsfläche die Adresse, geht der Weg über
          sie, und die Rückmeldung reist mit. Nennt ein älterer Server keine
          Nummer, steht die Meldung über der Liste — eine falsche Nummer zu
          raten wäre schlechter als keine.
        */
        const neueNummer = antwort.buchungsnummer ?? nNummer.trim();
        if (neueNummer === '') {
          setzeMeldung(angelegt);
          setzeNeuOffen(false);
        } else if (beiBuchung !== undefined) {
          beiBuchung(neueNummer, angelegt);
        } else {
          setzeMeldung(angelegt);
          setzeEigenesAnlegen(false);
          setzeNummer(neueNummer);
          await oeffne(neueNummer);
        }
      } catch (f) {
        setzeFehler(f instanceof ApiFehler ? f : null);
        if (!(f instanceof ApiFehler)) throw f;
      } finally {
        setzeLaeuft(false);
      }
    },
    [
      nTermin, nKunde, nPlaetze, nNummer, nWeg, nNachgebucht, nFrist,
      nBestellreferenz, nKostenstelle, nBesteller, gewaehlterKunde, anmelder,
      nAnmelderDabei, nTeilnehmer, beiBuchung, setzeNeuOffen, oeffne,
    ],
  );

  const belegen = useCallback(
    async (vorgang: string) => {
      if (buchung === null) return;
      setzeLaeuft(true);
      setzeFehler(null);
      setzeBelegmeldung(null);
      try {
        const beleg = await belegErzeugen(vorgang);
        setzeNachfrage(null);
        setzeBelegmeldung(beleg.hinweise);
        await aufDieSeite(async () => {
          setzeHistorie(await ladeVorgaenge(buchung.id));
        });
      } finally {
        setzeLaeuft(false);
      }
    },
    [buchung, aufDieSeite],
  );

  /*
   * BUC-146, BUC-148 (AeA-58, AeA-59): Gebühr senken und Storno zurücknehmen.
   * Betrag und Grund nimmt ein Formular an der Zeile auf (wie in der
   * Auftragsakte); ob es geht (Beleg, Termin, freie Plätze, Recht),
   * entscheidet der Server und sagt warum.
   */
  const korrigieren = useCallback(
    async (
      vorgang: string,
      wie: 'herabsetzen' | 'zuruecknehmen',
      eingabe: { readonly betrag: string | null; readonly grund: string },
    ) => {
      if (buchung === null) return;
      setzeLaeuft(true);
      setzeFehler(null);
      try {
        if (wie === 'herabsetzen' && eingabe.betrag !== null) {
          await herabsetzen(vorgang, eingabe.betrag, eingabe.grund);
        } else {
          await stornoZuruecknehmen(vorgang, eingabe.grund);
        }
        // Erst der Erfolg schließt Rückfrage und Formular; bei Abbruch oder Fehler bleiben Betrag und Grund stehen.
        setzeNachfrage(null);
        setzeKorrektur(null);
        await aufDieSeite(async () => {
          // Die Buchung selbst ändert sich bei der Rücknahme: Plätze und Status sind zurück.
          if (wie === 'zuruecknehmen') setzeBuchung(await ueberNummer(buchung.buchungsnummer));
          setzeHistorie(await ladeVorgaenge(buchung.id));
        });
      } finally {
        setzeLaeuft(false);
      }
    },
    [buchung, aufDieSeite],
  );

  /* Die Plaetze, deren E-Mail der Server nicht annehmen wuerde (ERR-03). */
  const falscheMails = unlesbareMails(Number(nPlaetze), nTeilnehmer);

  /* C3.4, C3.7: Beim Teil-Vorgang laesst sich waehlen, wen er betrifft. */
  const auswahlNoetig =
    buchung !== null
    && art !== 'teilnehmerwechsel'
    && teilweise(buchung.plaetze, Number(plaetze))
    && eigeneTeilnehmer.length > 0;

  /*
   * WAS UMZIEHT, MUSS INS ZIEL PASSEN (BUC-51, FRS-15).
   *
   * Ein leeres Plätze-Feld heißt am Server „die ganze Buchung"
   * (`plaetze ?? buchung.plaetze`) — dann müssen alle Plätze frei sein, nicht
   * einer. Bis CRM-94 sperrte die Auswahl hier nach `plaetze || '1'` und bot
   * für eine Buchung über vier Plätze einen Termin mit einem freien an.
   */
  const umziehend = buchung === null ? 1 : umziehendePlaetze(buchung.plaetze, plaetze);
  const ziel = ziele.find((z) => z.id === zielTermin);
  const zielZuKlein = ziel !== undefined && ziel.frei < umziehend;

  const gesperrt =
    buchung === null
    || vorschau === null
    || vorschau.nichtVorgesehen
    || laeuft
    || buchung.status === 'storniert'
    || (art === 'umbuchung' && (zielTermin.trim() === '' || zielZuKlein))
    /* Eine Auswahl muss genau die Platzzahl treffen -- sonst weist der Server ab. */
    || (auswahlNoetig && auswahl.length > 0 && auswahl.length !== Number(plaetze));

  /*
   * DIE VORGÄNGE DER BUCHUNG ALS TABELLE, mit „Beleg erzeugen" an jedem ohne
   * Beleg (RNG-03). Zweimal gebraucht (Auftrag #36): im Verlauf alle, unter
   * „Storno und Umbuchung" die offenen — dort ist der Beleg der letzte Schritt
   * der Abfolge.
   */
  const vorgangstabelle = (zeilen: readonly Vorgangszeile[]): JSX.Element => (
    <div className="tabellenhuelle tabellenhuelle--telefon">
      <table className="liste__tabelle">
        <caption className="nur-vorlesen">Vorgänge zu dieser Buchung</caption>
        <thead>
          <tr>
            <th scope="col">Zugang</th>
            <th scope="col">Art</th>
            <th scope="col">Plätze</th>
            <th scope="col">Gebühr</th>
            <th scope="col">Beleg</th>
          </tr>
        </thead>
        <tbody>
          {zeilen.map((v) => (
            <Fragment key={v.id}>
            <tr>
              <td className="liste__einzeilig">{datum(v.zugangAm)}</td>
              <td>{VORGANGSNAME[v.art]}</td>
              <td>{v.plaetze}</td>
              <td>
                {alsEuro(v.herabgesetztAuf ?? v.gebuehr)}
                {v.herabgesetztAuf !== null && (
                  <span className="marke">
                    herabgesetzt von {alsEuro(v.gebuehr)}
                  </span>
                )}
                {v.zurueckgenommenAm !== null && <span className="marke">zurückgenommen</span>}
              </td>
              <td>
                {v.beleg === null ? (
                  /*
                   * Ein Vorgang ohne Beleg ist offen — und der Knopf
                   * steht daneben, statt dass jemand ihn in der
                   * Faktura suchen muesste. Kostet der Vorgang
                   * nichts und ist nichts zu erstatten, weist der
                   * Server ihn ab: Ein Beleg ueber 0,00 Euro
                   * verbraucht eine Nummer, ohne etwas zu bezeugen
                   * (BP-16).
                   */
                  <button
                    type="button"
                    className="knopf knopf--klein"
                    disabled={laeuft}
                    onClick={() => {
                      setzeNachfrage({ art: 'beleg', vorgang: v });
                    }}
                  >
                    Beleg erzeugen …
                  </button>
                ) : (
                  (v.rechnungsnummer ?? `${v.belegart ?? ''} (Entwurf)`)
                )}
                {darfHerabsetzen && v.art === 'storno' && v.zurueckgenommenAm === null && v.beleg === null && (
                  <button
                    type="button"
                    className="knopf knopf--klein"
                    disabled={laeuft}
                    onClick={() => {
                      setzeKorrektur({ vorgang: v.id, art: 'herabsetzen' });
                    }}
                  >
                    Gebühr herabsetzen
                  </button>
                )}
                {v.art === 'storno' && v.zurueckgenommenAm === null && v.belegstatus !== 'festgeschrieben' && (
                  <button
                    type="button"
                    className="knopf knopf--klein"
                    disabled={laeuft}
                    onClick={() => {
                      setzeKorrektur({ vorgang: v.id, art: 'zuruecknehmen' });
                    }}
                  >
                    Storno zurücknehmen
                  </button>
                )}
                {v.art === 'storno' && v.zurueckgenommenAm === null && v.belegstatus === 'festgeschrieben' && (
                  <span className="feld__hilfe">Beleg festgeschrieben: nur eine neue Buchung.</span>
                )}
              </td>
            </tr>
            {korrektur?.vorgang === v.id && (
              <tr>
                <td colSpan={5}>
                  <Stornokorrektur
                    id={`korrektur-${v.id}`}
                    art={korrektur.art}
                    gebuehr={v.herabgesetztAuf ?? v.gebuehr}
                    laeuft={laeuft}
                    mitRueckfrage
                    beiAbbrechen={() => setzeKorrektur(null)}
                    beiBestaetigen={(eingabe) => {
                      setzeNachfrage({ art: 'korrektur', vorgang: v, wie: korrektur.art, ...eingabe });
                    }}
                  />
                </td>
              </tr>
            )}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );

  /* RNG-03: der angelegte Beleg — wo immer „Beleg erzeugen" gedrückt wurde. */
  const belegkarte =
    belegmeldung === null ? null : (
      <div className="karte" role="status">
        <span className="marke marke--gut">Beleg angelegt</span> Er steht als Entwurf in der Faktura;
        die Nummer entsteht mit der Festschreibung.
        {belegmeldung.map((h) => (
          <p key={h} className="feld__fehler-satz">
            {h}
          </p>
        ))}
      </div>
    );

  /* BUC-143: Verbrauch und Restfrist des Flextarifs — in der Übersicht und dort, wo umgebucht wird. */
  const flextarifmarke =
    buchung === null || buchung.vertragsart !== 'flextarif' ? null : buchung.flextarifVerbrauchtAm === null ? (
      <span className="marke marke--gut">kostenfreie Umbuchung offen</span>
    ) : (
      <span className="marke">
        kostenfreie Umbuchung verbraucht am {datum(buchung.flextarifVerbrauchtAm)}
      </span>
    );

  /*
   * WAS IN DER AKTE OFFEN IST (Auftrag #36) — je Reiter ein Satz, als Marke
   * an seinem Reiter. `undefined`: nichts offen.
   */
  const warnungen: Readonly<Partial<Record<Buchungsreiter, string>>> = {
    /* BUC-70: fehlende Teilnehmernamen. */
    ...(buchung !== null && buchung.offeneTeilnehmer > 0
      ? {
          teilnehmer:
            buchung.offeneTeilnehmer === 1
              ? 'ein Teilnehmername fehlt'
              : `${String(buchung.offeneTeilnehmer)} Teilnehmernamen fehlen`,
        }
      : {}),
    /* DAT-11, WEB-12: eine Zahlung, die nicht bestätigt ist; BUC-43: eine Rechnung, die nicht steht. */
    ...(buchung?.zahlung?.status === 'angelegt'
      ? { beleg: 'Zahlung offen' }
      : buchung?.zahlung?.status === 'fehlgeschlagen'
        ? { beleg: 'Zahlung fehlgeschlagen' }
        : faktura?.stand === 'entwurf' || faktura?.stand === 'fehler'
          ? { beleg: 'Rechnung nicht angelegt' }
          : {}),
    /* BUC-25, BUC-123: eine Zustimmung, die die Buchung noch schuldet. */
    ...(rechtsstand !== null
      && (rechtsstand.offen.length > 0 || rechtsstand.datenDritterOffen || rechtsstand.vorzeitigerBeginnOffen)
      ? { bedingungen: 'Zustimmung offen' }
      : {}),
  };

  /* Die Warnungen fürs Vorleseprogramm und den Kopf — in der Folge der Reiter. */
  const offeneSachen = BUCHUNGSREITER.flatMap((r) => (warnungen[r] === undefined ? [] : [warnungen[r]]));

  /*
   * DER AKTENKOPF DER BUCHUNG: „Buchung · Nummer", der Kunde als Titel, der
   * Status daneben, darunter Termin, Plätze, Vertragsart, Auftrag und die
   * Meldefrist der Teilnehmer — in Rost, solange Namen fehlen (BUC-70).
   *
   * Seit Auftrag #36 steht er über allen Reitern und trägt dazu den Betrag
   * (BUC-122), den Zahlungsstand (DAT-11) und, was offen ist — so sucht man
   * nie, um welche Buchung es geht und was an ihr hängt.
   */
  const kopfmelder = useRef(beiKopf);
  kopfmelder.current = beiKopf;
  const wegeImKopf = useRef(wege);
  wegeImKopf.current = wege;
  useEffect(() => {
    const melde = kopfmelder.current;
    if (melde === undefined) return;
    if (buchung === null) {
      /* Auftrag CRM-175: Das Anlegen ist eine Erfassung (ÄA-41) mit eigenem Titel. */
      melde(
        neuOffen
          ? {
              titel: 'Neue Buchung',
              unterzeile: 'Kunde, Produkt und Termin wählen, dann buchen',
              kennzeichen: undefined,
            }
          : {
              titel: 'Buchungen',
              unterzeile: 'Buchung finden: umbuchen, stornieren, Teilnehmer und Zusatzleistungen',
              kennzeichen: undefined,
            },
      );
      return;
    }
    /*
     * Auftrag CRM-95: der niedrige Kopf, ohne Unterzeile — sie trug nur das
     * Datum, das als „Termin" in der Kennzahlenleiste steht.
     */
    melde({
      kicker: `Buchung · ${buchung.buchungsnummer}`,
      titel: buchung.kundenname,
      sinnbild: 'ticket',
      kompakt: true,
      kennzeichen: (
        <span className={buchung.status === 'gebucht' ? 'marke marke--gut' : 'marke marke--warn'}>
          {buchung.status}
        </span>
      ),
      kennzahlen: (
        <Kennzahlenleiste
          zahlen={[
            { beschriftung: 'Termin', wert: datum(buchung.beginn) },
            { beschriftung: 'Plätze', wert: String(buchung.plaetze) },
            { beschriftung: 'Vertragsart', wert: buchung.vertragsart },
            {
              beschriftung: 'Auftrag',
              wert:
                buchung.auftragsnummer === null ? (
                  '—'
                ) : (
                  <Aktenverweis ziel="auftrag" id={buchung.auftrag} wege={wegeImKopf.current}>
                    {buchung.auftragsnummer}
                  </Aktenverweis>
                ),
            },
            {
              beschriftung: 'Teilnehmer melden bis',
              wert: buchung.teilnehmerFrist === null ? '—' : datum(buchung.teilnehmerFrist),
              frist: buchung.offeneTeilnehmer > 0,
            },
            /* BUC-122: die Gesamtsumme der Übersicht — erst da, wenn sie gerechnet ist. */
            { beschriftung: 'Betrag', wert: uebersicht === null ? '—' : alsEuro(uebersicht.gesamtsumme) },
            /* RNG-27 (AP-FA-03): was noch zu zahlen ist, vom Server gerechnet. */
            { beschriftung: 'Offen', wert: uebersicht === null ? '—' : alsEuro(uebersicht.offen) },
            /* DAT-11: „angelegt" ist nicht „bezahlt" — offen steht in Rost. */
            {
              beschriftung: 'Zahlung',
              wert: zahlungswort(buchung.zahlung?.status ?? null),
              frist: buchung.zahlung?.status === 'angelegt' || buchung.zahlung?.status === 'fehlgeschlagen',
            },
            ...(offeneSachen.length === 0
              ? []
              : [{ beschriftung: 'Offen', wert: offeneSachen.join(' · '), frist: true }]),
          ]}
        />
      ),
    });
    /* `offeneSachen` als Text: Das Objekt entsteht bei jedem Bild neu. */
  }, [buchung, uebersicht, offeneSachen.join('|'), neuOffen]);

  /*
   * LISTE ODER BUCHUNG (Auftrag CRM-70; Ulf, 06.10.2026): „beim öffnen einer
   * Buchung wird der knopf suchen und das Suchfeld nicht mehr angezeigt."
   * Ohne geöffnete Buchung zeigt die Maske die Liste mit Filtern, Suche und
   * Anlegen; mit ihr nur die Buchung. Nennt die Adresse eine Nummer, ist es
   * die Buchung — auch solange sie noch lädt oder nicht gefunden wurde.
   *
   * Den Verweis „← Alle Buchungen" darüber gibt es seit Auftrag CRM-95 nicht
   * mehr: Zurück führen der Menüpunkt „Buchungen" und die Zurück-Taste des
   * Browsers, beide mit den Filtern, die die Buchung aus der Liste mitnahm
   * (`Arbeitsflaeche.tsx`).
   */
  const listenansicht = buchung === null && (beiListe === undefined || (mitgebracht ?? '').trim() === '');

  /*
   * DIE RÜCKFRAGE (CRM-103) nennt, was die Maske schon weiß — die Vorschau,
   * die Zeile, das Versandbild. Erfunden wird nichts: Ob ein Portalzugang die
   * Buchung kennt und vom Storno erfährt, steht hier nicht und wird deshalb
   * auch nicht behauptet.
   */
  const rueckfrage = (): JSX.Element | null => {
    if (nachfrage === null || buchung === null) return null;
    const schliessen = (): void => setzeNachfrage(null);
    const unterzeile = `${buchung.buchungsnummer} · ${buchung.kundenname}`;

    switch (nachfrage.art) {
      case 'ausfuehren': {
        if (vorschau === null) return null;
        const anzahl = Number(plaetze);
        const betroffen = Number.isInteger(anzahl) && anzahl > 0 ? anzahl : buchung.plaetze;
        const frage =
          art === 'storno'
            ? 'Buchung stornieren?'
            : art === 'umbuchung'
              ? 'Buchung umbuchen?'
              : 'Teilnehmerwechsel ausführen?';
        const tat = art === 'storno' ? 'Stornieren' : art === 'umbuchung' ? 'Umbuchen' : 'Wechsel ausführen';
        return (
          <Rueckfrage frage={frage} unterzeile={unterzeile} tat={tat} beiBestaetigen={ausfuehren} beiSchliessen={schliessen}>
            <p>
              {betroffen === buchung.plaetze
                ? `Alle ${String(buchung.plaetze)} ${buchung.plaetze === 1 ? 'Platz' : 'Plätze'}`
                : `${String(betroffen)} von ${String(buchung.plaetze)} Plätzen`}
              {art === 'umbuchung' && ziel !== undefined
                ? ` ziehen auf den Termin am ${new Date(ziel.beginn).toLocaleString('de-DE', { timeZone: 'Europe/Berlin' })} um.`
                : '.'}{' '}
              Zugang der Erklärung: {datum(zugangAm)}
              {art === 'storno' && herkunft.weg !== '' ? `, ${ERKLAERUNGSWEGNAME[herkunft.weg]}` : ''}.
            </p>
            <p>
              Gebühr {alsEuro(vorschau.gebuehr)}
              {vorschau.satzProzent !== null
                ? ` (${Number(vorschau.satzProzent).toLocaleString('de-DE')} % ab ${String(vorschau.stufeAbTagen)} Tagen, Staffel ${vorschau.staffel})`
                : ''}
              {Number(vorschau.verrechnet) > 0 ? `, bereits gezahlt und verrechnet ${alsEuro(vorschau.verrechnet)}` : ''}
              . Zu erstatten {alsEuro(vorschau.zuErstatten)}, zu berechnen {alsEuro(vorschau.zuBerechnen)}.
              {vorschau.flextarifGenutzt ? ' Flextarif: kostenfrei, das Recht gilt danach als verbraucht.' : ''}
            </p>
            {grund.trim() !== '' && <p>Grund: {grund.trim()}</p>}
            {art === 'storno' && (
              // BUC-148: zurücknehmbar, solange kein Stornobeleg festgeschrieben ist.
              <p className="feld__hilfe">
                Zurücknehmen lässt sich der Storno, solange kein Stornobeleg festgeschrieben ist, der
                Termin nicht begonnen hat und die Plätze noch frei sind.
              </p>
            )}
          </Rueckfrage>
        );
      }

      case 'zusatz': {
        const z = nachfrage.leistung;
        return (
          <Rueckfrage
            frage="Zusatzleistung stornieren?"
            unterzeile={unterzeile}
            tat="Stornieren"
            beiBestaetigen={() => zusatzStornieren(z.id)}
            beiSchliessen={schliessen}
          >
            <p>
              {z.bezeichnung} für{' '}
              {z.teilnehmer === null
                ? 'die ganze Buchung'
                : `Platz ${String(z.position ?? '?')} — ${z.teilnehmername ?? 'ohne Namen'}`}
              , Menge {z.menge}, Einzelpreis {z.einzelpreis === null ? 'keiner' : alsEuro(z.einzelpreis)}.
            </p>
          </Rueckfrage>
        );
      }

      case 'korrektur': {
        const v = nachfrage.vorgang;
        const bisher = v.herabgesetztAuf ?? v.gebuehr;
        const eingabe = { betrag: nachfrage.betrag, grund: nachfrage.grund };
        return nachfrage.wie === 'herabsetzen' ? (
          // BUC-146: nur ohne Beleg; der Grund steht im Formular darunter und wird hier nur genannt.
          <Rueckfrage
            frage="Stornogebühr herabsetzen?"
            unterzeile={`${unterzeile} · Storno vom ${datum(v.zugangAm)}`}
            tat="Herabsetzen"
            beiBestaetigen={() => korrigieren(v.id, 'herabsetzen', eingabe)}
            beiSchliessen={schliessen}
          >
            <p>
              Die Gebühr sinkt von {alsEuro(bisher)} auf {alsEuro(nachfrage.betrag ?? bisher)}.
            </p>
            <p>Grund: {nachfrage.grund}</p>
          </Rueckfrage>
        ) : (
          // BUC-148: Plätze wieder belegt, Gebühr entfällt, Belegentwurf verworfen.
          <Rueckfrage
            frage="Storno zurücknehmen?"
            unterzeile={`${unterzeile} · Storno vom ${datum(v.zugangAm)}`}
            tat="Zurücknehmen"
            beiBestaetigen={() => korrigieren(v.id, 'zuruecknehmen', eingabe)}
            beiSchliessen={schliessen}
          >
            <p>
              {v.plaetze} {v.plaetze === 1 ? 'Platz wird' : 'Plätze werden'} wieder belegt, die Gebühr von{' '}
              {alsEuro(bisher)} entfällt
              {v.beleg === null ? '' : ', und der Belegentwurf zum Storno wird verworfen'}.
            </p>
            <p>Grund: {nachfrage.grund}</p>
          </Rueckfrage>
        );
      }

      case 'beleg': {
        const v = nachfrage.vorgang;
        /*
         * ÄA-62 (B): Die Rückfrage bleibt, obwohl nur ein Entwurf entsteht. Er
         * bindet den Vorgang und sperrt das Herabsetzen (BUC-146); wer den
         * Entwurf löscht, gibt den Vorgang nicht frei — das tut nur die
         * Rücknahme (BUC-148).
         */
        return (
          <Rueckfrage
            frage="Beleg erzeugen?"
            unterzeile={`${unterzeile} · ${VORGANGSNAME[v.art]} vom ${datum(v.zugangAm)}`}
            tat="Erzeugen"
            beiBestaetigen={() => belegen(v.id)}
            beiSchliessen={schliessen}
          >
            <p>
              {VORGANGSNAME[v.art]} über {v.plaetze} {v.plaetze === 1 ? 'Platz' : 'Plätze'}, Gebühr{' '}
              {alsEuro(v.herabgesetztAuf ?? v.gebuehr)}
              {v.herabgesetztAuf !== null ? ` (herabgesetzt von ${alsEuro(v.gebuehr)})` : ''}. Der Beleg
              entsteht als Entwurf.
            </p>
            {/* BUC-146: Herabsetzen geht nur ohne Beleg. */}
            {v.art === 'storno' && v.zurueckgenommenAm === null && (
              <p className="feld__hilfe">Danach lässt sich die Gebühr nicht mehr herabsetzen.</p>
            )}
          </Rueckfrage>
        );
      }

      case 'belegzeilen':
        return (
          // BUC-121: Was schon berechnet ist, bleibt; der Rest wird nach der Übersicht neu geschrieben.
          <Rueckfrage
            frage="Belegpositionen neu erzeugen?"
            unterzeile={unterzeile}
            tat="Neu erzeugen"
            beiBestaetigen={belegzeilenErzeugen}
            beiSchliessen={schliessen}
          >
            <p>
              Am Auftrag {buchung.auftragsnummer ?? '—'} {buchung.belegzeilen === 1 ? 'steht' : 'stehen'}{' '}
              {buchung.belegzeilen} Belegposition{buchung.belegzeilen === 1 ? '' : 'en'} zu dieser Buchung.
              Die noch nicht berechneten werden ersetzt — je Teilnehmer eine, nach der Übersicht; bereits
              berechnete bleiben stehen.
            </p>
          </Rueckfrage>
        );

      case 'versand': {
        const empfaenger = [...new Set((versandbild?.zeilen ?? []).map((z) => z.empfaenger))];
        const texte = [...new Set((versandbild?.zeilen ?? []).map((z) => z.bezeichnung))];
        const erneut = nachfrage.anlass === 'erneut';
        return (
          // BUC-78: Empfänger und Texte stehen vor dem Versand.
          <Rueckfrage
            frage={erneut ? 'Bedingungen erneut verschicken?' : 'Bedingungen verschicken?'}
            unterzeile={unterzeile}
            tat={erneut ? 'Erneut verschicken' : 'Verschicken'}
            beiBestaetigen={() => versenden(nachfrage.anlass)}
            beiSchliessen={schliessen}
          >
            <p>
              {texte.length} {texte.length === 1 ? 'Text geht' : 'Texte gehen'} an {empfaenger.join(', ') || '—'}
              {texte.length > 0 ? `: ${texte.join(', ')}` : ''}. Eine verschickte Nachricht lässt sich nicht
              zurückholen.
            </p>
            {versandbild?.probeversand === true && (
              <p className="warnung">
                Probeversand eingeschaltet — die Nachricht zählt nicht als Übermittlung.
              </p>
            )}
          </Rueckfrage>
        );
      }

      case 'bestaetigung':
        return nachfrage.gegenstand === 'daten_dritter' ? (
          // BUC-123
          <Rueckfrage
            frage="Berechtigung für Teilnehmerdaten bestätigen?"
            unterzeile={unterzeile}
            tat="Bestätigen"
            beiBestaetigen={() => bestaetigen('daten_dritter')}
            beiSchliessen={schliessen}
          >
            <p>
              Festgehalten wird, dass der Anmelder die Daten der Teilnehmer angeben darf und sie
              informiert hat.
            </p>
          </Rueckfrage>
        ) : (
          // BUC-25, § 356 Abs. 4 BGB
          <Rueckfrage
            frage="Vorzeitigen Leistungsbeginn bestätigen?"
            unterzeile={unterzeile}
            tat="Bestätigen"
            beiBestaetigen={() => bestaetigen('vorzeitiger_beginn')}
            beiSchliessen={schliessen}
          >
            <p>
              Festgehalten wird die ausdrückliche Zustimmung des Kunden zum Beginn der Leistung vor
              Ablauf der Widerrufsfrist. Ohne sie kann er auch nach dem Training noch widerrufen
              (§ 356 Abs. 4 BGB).
            </p>
          </Rueckfrage>
        );
    }
  };

  return (
    <div className="stapel">
      {/*
        * Auftrag CRM-175 (Ulf, 10.10.2026): Im Anlegen stehen weder die Reihe
        * zum Suchen und Öffnen noch die Liste — die Maske zeigt nur das Anlegen.
        */}
      {listenansicht && !neuOffen && (
        <form className="karte" onSubmit={suche}>
          <div className="feld">
            <label className="feld__beschriftung" htmlFor="buchungsnummer">
              {/*
                * "suchen" gehoert in die Beschriftung: Weiter unten vergibt ein
                * zweites Feld eine NEUE Buchungsnummer. Zwei Felder mit
                * derselben Beschriftung sind fuer eine Vorlesehilfe nicht
                * unterscheidbar — und fuer einen Menschen auch nicht.
                */}
              Buchungsnummer suchen
            </label>
            <input
              id="buchungsnummer"
              className="feld__eingabe"
              value={nummer}
              onChange={(e) => {
                setzeNummer(e.target.value);
              }}
              placeholder="wie auf dem Beleg des Kunden"
            />
          </div>
          <div className="knopfreihe">
            <button type="submit" className="knopf knopf--haupt" disabled={laeuft}>
              Öffnen
            </button>
            {/*
              BUC-80 (19.09.2026): Wer die Nummer nicht hat, sucht über Namen,
              Produkt, Zeitraum oder Status — in einem eigenen Fenster.
            */}
            <button
              type="button"
              className="knopf"
              onClick={() => {
                setzeFenster('buchung');
              }}
            >
              Buchung suchen
            </button>
            {/*
              * BUC-50/BUC-51: eine Buchung anlegen — seit Auftrag CRM-70 in
              * derselben Reihe über der Liste; bei geöffneter Buchung nicht.
              * Auf dem Telefon ab Tablet (Auftrag CRM-106, ÄA-63): Suchen ja,
              * Anlegen nein. Abbrechen steht seit CRM-175 im Anlegen selbst.
              */}
            {!telefon && (
              <button
                type="button"
                className="knopf"
                onClick={() => {
                  setzeNeuOffen(true);
                }}
              >
                Buchung anlegen
              </button>
            )}
          </div>
        </form>
      )}

      {fehler !== null && <Meldung fehler={fehler} gezeigteFelder={['grundbetrag']} />}
      {meldung !== null && (
        <p className="hinweiszeile" role="status">
          {meldung}
        </p>
      )}

      {listenansicht && neuOffen && !telefon && (
        <Erfassung
          beschriftung="Neue Buchung"
          beiAbsenden={() => void buchen()}
          absenden={{
            text: 'Buchen',
            gesperrt:
              laeuft
              || nTermin.trim() === ''
              || nKunde.trim() === ''
              /* Das Formular prüft nicht selbst (`noValidate`) — die Platzzahl hier. */
              || !(Number.isInteger(Number(nPlaetze)) && Number(nPlaetze) >= 1)
              /* ERR-03: Eine unlesbare Adresse wird hier gemeldet, nicht vom Server. */
              || falscheMails.length > 0,
          }}
          /* Auftrag CRM-175: Abbrechen führt zur Liste zurück, mit ihren Filtern. */
          abbrechen={{ text: 'Abbrechen', beiKlick: () => setzeNeuOffen(false) }}
          zusatz={
            falscheMails.length > 0 && (
              <span className="feld__hilfe">
                {falscheMails.length === 1
                  ? `Platz ${String(falscheMails[0])} trägt keine lesbare E-Mail-Adresse.`
                  : `Die Plätze ${falscheMails.join(', ')} tragen keine lesbare E-Mail-Adresse.`}
              </span>
            )
          }
        >
          <Abschnitt titel="Buchung">
            <div className="feld">
              <label className="feld__beschriftung" htmlFor="nNummer">
                Buchungsnummer
              </label>
              <input
                id="nNummer"
                className="feld__eingabe"
                value={nNummer}
                onChange={(e) => {
                  setzeNNummer(e.target.value);
                }}
              />
              <p className="feld__hilfe">
                Leer lassen — dann vergibt der Nummernkreis sie beim Anlegen, lückenlos und
                protokolliert. Eintragen nur, wenn die Buchung aus dem Portal eine eigene
                Nummer mitbringt.
              </p>
            </div>
            {/*
              * ZUERST DER KUNDE — UND ZWAR AUS ZWEI GRÜNDEN.
              *
              * BUC-105: „Die Kundenart bestimmt, welche Produkte ... überhaupt
              * angeboten werden. Die API liefert nur das jeweils Zulässige."
              * Ohne den Kunden wüsste die Maske nicht, was sie anbieten darf.
              *
              * KAP-04: Die freie Kapazität hängt am Anfragenden. Kontingent-
              * plätze, die ihm zustehen, zählen für ihn als frei. Ohne ihn
              * rechnete die Terminliste die anonyme Sicht (KAP-22) — und meldete
              * einem Kunden mit eigenem Kontingent „voll", obwohl Plätze für ihn
              * zurückliegen.
              *
              * Die Reihenfolge war umgekehrt, solange beides nicht galt. Sie
              * war deshalb nicht falsch, sondern unvollständig.
              *
              * DIE KUNDENSUCHE STATT EINER LISTE: Eine Auswahlliste aller
              * Kunden ist keine Auswahl. Gesucht wird ab zwei Zeichen — bei
              * einem leeren Feld lieferte sie die ersten Kunden des Hauses und
              * bei jedem Tastendruck eine Abfrage.
              */}
            <div className="feld">
              <label className="feld__beschriftung" htmlFor="nKundeAnzeige">
                Kunde <span className="feld__pflicht">Pflicht</span>
              </label>
              <div className="wahlfeld">
                <input
                  id="nKundeAnzeige"
                  className="feld__eingabe"
                  readOnly
                  value={
                    gewaehlterKunde === undefined
                      ? ''
                      : `${gewaehlterKunde.kundennummer} — ${gewaehlterKunde.name}`
                      + (gewaehlterKunde.ort === null ? '' : `, ${gewaehlterKunde.ort}`)
                  }
                  placeholder="— über die Suche wählen —"
                  onClick={() => {
                    setzeFenster('kunde');
                  }}
                />
                <button
                  type="button"
                  className="knopf"
                  onClick={() => {
                    setzeFenster('kunde');
                  }}
                >
                  Kunde suchen
                </button>
              </div>
              {gewaehlterKunde?.gesperrt === true && (
                <p className="feld__hilfe">Dieser Kunde ist gesperrt.</p>
              )}
            </div>
            {/*
              BUC-103, BUC-127: Beim Geschäftskunden meldet ein Ansprechpartner
              an. Er ist der Besteller — und kann zugleich Teilnehmer sein.
            */}
            {kundenakte !== null
              && kundenakte.id === nKunde
              && kundenakte.kundenart !== 'privat' && (
              <div className="feld">
                <label className="feld__beschriftung" htmlFor="nBesteller">
                  Anmelder (Ansprechpartner)
                </label>
                <select
                  id="nBesteller"
                  className="feld__eingabe"
                  value={nBesteller}
                  onChange={(e) => {
                    setzeNBesteller(e.target.value);
                  }}
                >
                  <option value="">— keiner genannt —</option>
                  {kundenakte.kontakte
                    .filter((k) => !k.ausgeschieden)
                    .map((k) => (
                      <option key={k.id} value={k.id}>
                        {[k.person.vorname, k.person.nachname].filter((t) => t !== null).join(' ')}
                        {k.funktion === null ? '' : ` (${k.funktion})`}
                      </option>
                    ))}
                </select>
              </div>
            )}
            {/*
              * DANN DAS PRODUKT, DANN DER TERMIN.
              *
              * Ein Terminfeld, das alle Termine des Hauses anböte, wäre nach dem
              * ersten Betriebsjahr eine Liste mit tausend Einträgen. Wer bucht,
              * weiß, WAS gebucht wird — danach sind es eine Handvoll Termine.
              *
              * Die Liste zeigt nur, was DIESER Kundenart angeboten wird
              * (BUC-105) — nicht das ganze Angebot mit Sperrhinweisen.
              */}
            <div className="feld">
              <label className="feld__beschriftung" htmlFor="nArtikel">
                Produkt <span className="feld__pflicht">Pflicht</span>
              </label>
              <div className="wahlfeld">
                <input
                  id="nArtikel"
                  className="feld__eingabe"
                  readOnly
                  disabled={nKunde === ''}
                  value={(() => {
                    const a = artikel.find((x) => x.id === nArtikel);
                    return a === undefined ? '' : `${a.artikelnummer} — ${a.bezeichnung}`;
                  })()}
                  placeholder={nKunde === '' ? '— zuerst einen Kunden wählen —' : '— über die Suche wählen —'}
                  onClick={() => {
                    if (nKunde !== '') setzeFenster('produkt');
                  }}
                />
                <button
                  type="button"
                  className="knopf"
                  disabled={nKunde === ''}
                  onClick={() => {
                    setzeFenster('produkt');
                  }}
                >
                  Produkt suchen
                </button>
              </div>
              {nKunde !== '' && artikel.length === 0 && (
                <p className="feld__hilfe">
                  Diesem Kunden wird derzeit kein Produkt angeboten.
                </p>
              )}
            </div>

            <div className="feld">
              <label className="feld__beschriftung" htmlFor="nTermin">
                Termin <span className="feld__pflicht">Pflicht</span>
              </label>
              <div className="wahlfeld">
                <input
                  id="nTermin"
                  className="feld__eingabe"
                  readOnly
                  disabled={nArtikel === ''}
                  value={(() => {
                    const t = terminwahl.find((z) => z.id === nTermin);
                    return t === undefined
                      ? ''
                      : `${new Date(t.beginn).toLocaleString('de-DE', { timeZone: 'Europe/Berlin' })} — `
                        + `${String(t.frei)} von ${String(t.hoechstteilnehmer)} frei`;
                  })()}
                  placeholder={nArtikel === '' ? '— zuerst ein Produkt wählen —' : '— im Kalender wählen —'}
                  onClick={() => {
                    if (nArtikel !== '') setzeFenster('termin');
                  }}
                />
                <button
                  type="button"
                  className="knopf"
                  disabled={nArtikel === ''}
                  onClick={() => {
                    setzeFenster('termin');
                  }}
                >
                  Kalender öffnen
                </button>
              </div>
              {nArtikel !== '' && terminwahl.length === 0 && (
                <p className="feld__hilfe">
                  Für dieses Produkt gibt es keinen buchbaren Termin.
                </p>
              )}
              {/*
                FRS-15: Ein gewählter Termin, der nach dem Ändern der Platzzahl
                nicht mehr reicht, wird genannt — der Server wiese ihn ohnehin ab.
              */}
              {(() => {
                const t = terminwahl.find((z) => z.id === nTermin);
                return t !== undefined && t.frei < Number(nPlaetze || '1') ? (
                  <p className="feld__hilfe">
                    Dieser Termin hat nur {t.frei} freie Plätze.
                  </p>
                ) : null;
              })()}
            </div>

            <div className="feld">
              <label className="feld__beschriftung" htmlFor="nPlaetze">
                Plätze <span className="feld__pflicht">Pflicht</span>
              </label>
              <input
                id="nPlaetze"
                type="number"
                min={1}
                className="feld__eingabe feld__eingabe--schmal"
                value={nPlaetze}
                onChange={(e) => {
                  setzeNPlaetze(e.target.value);
                }}
              />
              <p className="feld__hilfe">
                Für jeden Platz entsteht eine Teilnehmerzeile — zunächst ohne Namen. Nur so
                erscheint sie in der Arbeitsliste.
              </p>
            </div>
            {/* BUC-80: die Preisauskunft der Aufnahmemaske — vor dem Buchen. */}
            <Preisauskunft auskunft={preis} laeuft={preisLaeuft} />
          </Abschnitt>
          <Abschnitt titel="Teilnehmer">
            <Teilnehmererfassung
              plaetze={Number(nPlaetze)}
              anmelder={anmelder}
              anmelderDabei={nAnmelderDabei}
              beiAnmelderDabei={setzeNAnmelderDabei}
              zeilen={nTeilnehmer}
              beiAenderung={(position, zeile) => {
                setzeNTeilnehmer((alt) => ({ ...alt, [position]: zeile }));
              }}
            />
          </Abschnitt>
          <Abschnitt titel="Weitere Angaben">
            <div className="feld">
              <label className="feld__beschriftung" htmlFor="nWeg">
                Buchungsweg
              </label>
              <select
                id="nWeg"
                className="feld__eingabe"
                value={nWeg}
                onChange={(e) => {
                  setzeNWeg(e.target.value as 'portal' | 'innendienst' | 'angebot');
                }}
              >
                <option value="innendienst">Innendienst</option>
                <option value="portal">Portal</option>
                <option value="angebot">Angebot</option>
              </select>
            </div>
            {/*
              * BUC-102/BUC-103: Diese Felder gibt es NUR beim Geschäftskunden.
              *
              * Ein abweichender Rechnungsempfänger beim Verbraucher wäre ein
              * Vertrag zu Lasten Dritter — und die Widerrufsbelehrung ginge an
              * jemanden, der nicht widerrufen kann. Die Datenbank weist es ab
              * (0069); hier steht das Feld erst gar nicht.
              */}
            {gewaehlterKunde !== undefined && gewaehlterKunde.kundenart !== 'privat' && (
              <>
                <div className="feld">
                  <label className="feld__beschriftung" htmlFor="nBestellreferenz">
                    Bestellreferenz
                  </label>
                  <input
                    id="nBestellreferenz"
                    className="feld__eingabe"
                    value={nBestellreferenz}
                    onChange={(e) => {
                      setzeNBestellreferenz(e.target.value);
                    }}
                  />
                </div>
                <div className="feld">
                  <label className="feld__beschriftung" htmlFor="nKostenstelle">
                    Kostenstelle
                  </label>
                  <input
                    id="nKostenstelle"
                    className="feld__eingabe feld__eingabe--schmal"
                    value={nKostenstelle}
                    onChange={(e) => {
                      setzeNKostenstelle(e.target.value);
                    }}
                  />
                </div>
              </>
            )}

            {gewaehlterKunde !== undefined && gewaehlterKunde.kundenart === 'privat' && (
              <p className="feld__hilfe">
                Privatkunde: Bruttopreise, Widerrufsbelehrung, Zahlung sofort. Bestellreferenz,
                Kostenstelle und ein abweichender Rechnungsempfänger sind hier nicht vorgesehen.
              </p>
            )}

            <div className="feld">
              <label className="feld__beschriftung" htmlFor="nFrist">
                Frist für die Teilnehmermeldung
              </label>
              <input
                id="nFrist"
                type="date"
                className="feld__eingabe feld__eingabe--schmal"
                value={nFrist}
                onChange={(e) => {
                  setzeNFrist(e.target.value);
                }}
              />
              <p className="feld__hilfe">
                Leer heißt „keine Frist vereinbart" und nicht „sofort" — ein Privatkunde, der für
                sich selbst bucht, hat nichts nachzureichen.
              </p>
            </div>
            <div className="feld">
              <label className="feld__beschriftung" htmlFor="nNachgebucht">
                <input
                  id="nNachgebucht"
                  type="checkbox"
                  checked={nNachgebucht}
                  onChange={(e) => {
                    setzeNNachgebucht(e.target.checked);
                  }}
                />{' '}
                Nachbuchung ohne Zahlungsstrecke
              </label>
              {/* BUC-51 */}
              <p className="feld__hilfe">
                Der Mitarbeiter bucht nach, es wird keine Zahlung angestoßen. Alles andere
                — Platzprüfung, Kapazität, Teilnehmer — ist gleich.
              </p>
            </div>
          </Abschnitt>
        </Erfassung>
      )}

      {/* BUC-80: die Fenster stehen AUSSERHALB der Formulare — ein Formular im Formular gibt es nicht. */}
      {fenster === 'buchung' && (
        <Buchungssuche
          nummer={nummer}
          beiSchliessen={() => {
            setzeFenster(null);
          }}
          beiWahl={(gewaehlt) => {
            setzeFenster(null);
            setzeNummer(gewaehlt);
            void waehleBuchung(gewaehlt);
          }}
        />
      )}
      {fenster === 'kunde' && (
        <Kundensuche
          beiSchliessen={() => {
            setzeFenster(null);
          }}
          beiWahl={(k) => {
            setzeFenster(null);
            setzeGewaehltenKunden(k);
            setzeNKunde(k.id);
            setzeNArtikel('');
            setzeNTermin('');
            setzeNTeilnehmer({});
          }}
        />
      )}
      {fenster === 'produkt' && (
        <Produktsuche
          artikel={artikel}
          beiSchliessen={() => {
            setzeFenster(null);
          }}
          beiWahl={(a) => {
            setzeFenster(null);
            setzeNArtikel(a.id);
            setzeNTermin('');
          }}
        />
      )}
      {fenster === 'termin' && (
        <Terminkalenderwahl
          termine={terminwahl}
          plaetze={Number(nPlaetze || '1')}
          gewaehlt={nTermin}
          beiSchliessen={() => {
            setzeFenster(null);
          }}
          beiWahl={(t) => {
            setzeFenster(null);
            setzeNTermin(t.id);
          }}
        />
      )}
      {fenster === 'zieltermin' && (
        <Terminkalenderwahl
          titel="Zieltermin wählen"
          termine={ziele}
          plaetze={umziehend}
          gewaehlt={zielTermin}
          beiSchliessen={() => {
            setzeFenster(null);
          }}
          beiWahl={(t) => {
            setzeFenster(null);
            setzeZielTermin(t.id);
          }}
        />
      )}
      {rueckfrage()}

      {/*
        * BUC-70: DIE ARBEITSLISTE STEHT JETZT UNTER SERVICE (B7, 12.09.2026).
        *
        * Sie stand hier — als Block unter der gesuchten Buchung, in einer Maske
        * ueber eine EINZELNE Buchung. Wer morgens fragt, was heute zu tun ist,
        * hat aber keine Buchungsnummer im Kopf, sondern eine Frist: Die Liste war
        * damit das Ergebnis einer Suche, die man vorher machen musste, um sie zu
        * sehen.
        *
        * Sie ist nach `buchung/Arbeitsliste.tsx` gezogen und ein eigener
        * Menuepunkt. Der Weg laeuft jetzt in die andere Richtung — von der Frist
        * in die Buchung, ueber `nummer`.
        */}

      {listenansicht && !neuOffen && (
        <Buchungsliste
          filter={filter}
          beiFilter={(neu) => {
            if (beiListe !== undefined) beiListe(neu, true);
            else setzeEigenenFilter(neu);
          }}
          beiOeffnen={(gewaehlt) => {
            setzeNummer(gewaehlt);
            void waehleBuchung(gewaehlt);
          }}
        />
      )}

      {buchung !== null && (
        /*
         * DIE BUCHUNGSAKTE MIT REITERN (Auftrag #36, ÄA-41).
         *
         * Bis zum 30.09.2026 stand hier: „KEINE REITER: Die Abschnitte sind eine
         * Abfolge." Das galt für den Storno — und für ihn gilt es weiter: Vorgang,
         * Berechnung und danach der Beleg stehen in EINEM Reiter („Storno und
         * Umbuchung"), untereinander, wie bisher. Die übrigen Abschnitte waren
         * keine Abfolge, sondern Nachbarn, und die Seite war lang genug, dass man
         * sie suchte. Der Auftraggeber: „die vielen Informationen mit einem
         * Untermenü aufteilen."
         *
         * Was man nie suchen soll — um welche Buchung es geht —, steht im Kopf
         * der Akte über allen Reitern: Nummer, Status, Kunde, Termin, Betrag,
         * Fristen. Eine offene Sache (fehlende Namen, offene Zahlung, fehlende
         * Zustimmung) trägt ihr Reiter als Marke, damit sie nicht hinter einem
         * anderen Reiter verschwindet.
         *
         * Der gewählte Reiter steht in der Adresse (`wegweiser.ts`); Arbeitsliste,
         * Absagen und Widerrufe springen in den passenden.
         */
        <div className="akte akte--eine-spalte">
          <section className="akte__haupt">
            <Reiterleiste
              beschriftung={`Buchung ${buchung.buchungsnummer}`}
              praefix="buchung-"
              gewaehlt={reiter}
              beiWahl={waehleReiter}
              reiter={BUCHUNGSREITER.map((kennung) => ({
                kennung,
                beschriftung: REITERNAME[kennung],
                warnung: warnungen[kennung],
              }))}
            />
            <div
              role="tabpanel"
              id={`tafel-buchung-${reiter}`}
              aria-labelledby={`reiter-buchung-${reiter}`}
              className="tafel__abschnitte"
            >
              {reiter === 'uebersicht' && (
                <>
                    <div className="karte">
                      <h2>
                        {buchung.buchungsnummer}{' '}
                        {buchung.status !== 'gebucht' && (
                          <span className="marke marke--warn">{buchung.status}</span>
                        )}
                      </h2>
                      <dl>
                        <dt>Anmelder</dt>
                        <dd>
                          {/*
                            * BUC-115: Der Anmelder ist Besteller und Vertragspartner —
                            * nicht zwingend ein Teilnehmer. Die Beschriftung sagt es,
                            * damit niemand ihn für den Fahrer hält.
                            */}
                          <Aktenverweis ziel="kunde" id={buchung.kunde} wege={wege}>
                            {buchung.kundenname}
                          </Aktenverweis>{' '}
                          ({buchung.kundenart})
                        </dd>
                        {/*
                          * BUC-106: die EINGEFRORENE Kundenart der Buchung. Weicht sie
                          * von der des Kunden ab, hat er seither gewechselt — und die
                          * Buchung bleibt bei dem, was bei ihrer Anlage galt.
                          */}
                        {buchung.buchungskundenart !== null
                          && buchung.buchungskundenart !== buchung.kundenart && (
                          <>
                            <dt>Kundenart der Buchung</dt>
                            <dd>
                              <span className="marke marke--warn">
                                {buchung.buchungskundenart}
                              </span>{' '}
                              Der Kunde hat die Art seither gewechselt. Für diese Buchung gilt weiter,
                              was bei ihrer Anlage galt.
                            </dd>
                          </>
                        )}
                        {buchung.preisfuehrung !== null && (
                          <>
                            <dt>Preisdarstellung</dt>
                            <dd>
                              {buchung.preisfuehrung === 'brutto'
                                ? 'Bruttopreise, Widerrufsbelehrung, Zahlung sofort'
                                : 'Nettopreise mit Steuerausweis, Zahlung auf Rechnung'}{' '}
                              <span className="hilfe"></span>
                            </dd>
                          </>
                        )}
                        {buchung.rechnungsempfaenger !== null && (
                          <>
                            <dt>Rechnungsempfänger</dt>
                            <dd>{buchung.rechnungsempfaengername ?? buchung.rechnungsempfaenger}</dd>
                          </>
                        )}
                        {(buchung.bestellreferenz !== null || buchung.kostenstelle !== null) && (
                          <>
                            <dt>Bestellreferenz / Kostenstelle</dt>
                            <dd>
                              {buchung.bestellreferenz ?? '—'} / {buchung.kostenstelle ?? '—'}
                            </dd>
                          </>
                        )}
                        <dt>Termin</dt>
                        <dd>
                          {beiTermin === undefined ? (
                            <>
                              {buchung.artikel}, {new Date(buchung.beginn).toLocaleString('de-DE')}
                            </>
                          ) : (
                            <Maskenverweis
                              maske={{ art: 'terminakte', id: buchung.termin }}
                              beiKlick={() => {
                                beiTermin(buchung.termin);
                              }}
                            >
                              {buchung.artikel}, {new Date(buchung.beginn).toLocaleString('de-DE')}
                            </Maskenverweis>
                          )}
                        </dd>
                        <dt>Plätze</dt>
                        <dd>{buchung.plaetze}</dd>
                        <dt>Vertragsart</dt>
                        <dd>
                          {buchung.vertragsart}
                          {/* BUC-143: Verbrauch und Restfrist sind auf der Buchung sichtbar. */}
                          {flextarifmarke !== null && <> {flextarifmarke}</>}
                        </dd>
                        {buchung.offeneTeilnehmer > 0 && (
                          <>
                            <dt>Offene Teilnehmer</dt>
                            <dd>
                              <span className="marke marke--warn">
                                {buchung.offeneTeilnehmer} Namen fehlen
                              </span>
                            </dd>
                          </>
                        )}
                      </dl>
                    </div>

                    {/*
                      * ========================================================
                      * DIE ÜBERSICHT VOR DEM ABSCHLUSS (BUC-122)
                      * ========================================================
                      *
                      * „zeigt JE TEILNEHMER den Preis, die berücksichtigte
                      * Mitgliedschaft und die gewählten Zusatzleistungen sowie die
                      * Gesamtsumme."
                      *
                      * Die Vorbehalte stehen NEBEN der Summe und nicht darunter: Eine
                      * Zahl, die nicht alles enthält, ist ohne diesen Satz eine falsche
                      * Zahl — und sieht aus wie eine richtige.
                      */}
                    {uebersicht !== null && (
                      <div className="karte">
                        <h2>Übersicht</h2>

                        {uebersicht.teilnehmer.map((tn) => (
                          <div className="block" key={tn.teilnehmer}>
                            <h3>
                              Platz {tn.position} — {tn.name ?? 'ohne Namen'}
                            </h3>
                            <div className="tabellenhuelle tabellenhuelle--telefon">
                              <table className="liste__tabelle">
                                <caption className="nur-vorlesen">
                                  Positionen für Platz {tn.position}
                                </caption>
                                <thead>
                                  <tr>
                                    <th scope="col">Position</th>
                                    <th scope="col">Menge</th>
                                    <th scope="col">Einzelpreis</th>
                                    <th scope="col">Gesamt</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {tn.zeilen.map((z, i) => (
                                    <tr key={`${tn.teilnehmer}-${String(i)}`}>
                                      <td>
                                        {z.bezeichnung}
                                        {/*
                                          * BUC-121: Ein abweichender Preis steht MIT
                                          * SEINEM GRUND da. Eine Zahl ohne Grund ist auf
                                          * einem Beleg nicht erklärbar.
                                          */}
                                        {z.preisgrund !== null && (
                                          <>
                                            {' '}
                                            <span className="kanalliste__name">{z.preisgrund}</span>
                                          </>
                                        )}
                                      </td>
                                      <td>{z.menge}</td>
                                      <td className="wert">{alsEuro(z.einzelpreis)}</td>
                                      <td className="wert">{alsEuro(z.gesamt)}</td>
                                    </tr>
                                  ))}
                                  <tr className="gesamt">
                                    <td colSpan={3}>Zwischensumme</td>
                                    <td className="wert">{alsEuro(tn.summe)}</td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          </div>
                        ))}

                        {uebersicht.fuerDieBuchung.length > 0 && (
                          <div className="block">
                            <h3>Für die ganze Buchung</h3>
                            <div className="tabellenhuelle tabellenhuelle--telefon">
                              <table className="liste__tabelle">
                                <caption className="nur-vorlesen">Leistungen der Buchung</caption>
                                <thead>
                                  <tr>
                                    <th scope="col">Position</th>
                                    <th scope="col">Menge</th>
                                    <th scope="col">Einzelpreis</th>
                                    <th scope="col">Gesamt</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {uebersicht.fuerDieBuchung.map((z, i) => (
                                    <tr key={`bu-${String(i)}`}>
                                      <td>{z.bezeichnung}</td>
                                      <td>{z.menge}</td>
                                      <td className="wert">{alsEuro(z.einzelpreis)}</td>
                                      <td className="wert">{alsEuro(z.gesamt)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}

                        <div className="tabellenhuelle tabellenhuelle--telefon">
                          <table className="summen">
                            <tbody>
                              <tr className="gesamt">
                                <td>
                                  Gesamtsumme
                                  {uebersicht.preisfuehrung !== null && (
                                    <span className="hilfe">
                                      {' '}
                                      ({uebersicht.preisfuehrung === 'brutto' ? 'brutto' : 'netto'})
                                    </span>
                                  )}
                                </td>
                                <td className="wert">
                                  <strong>{alsEuro(uebersicht.gesamtsumme)}</strong>
                                </td>
                              </tr>
                              {/* RNG-27, PRF-17 (AP-FA-03): was davon abgeht und was offen bleibt. */}
                              {uebersicht.eingegangen !== '0.00' && (
                                <tr>
                                  <td>abzüglich eingegangen</td>
                                  <td className="wert">{alsEuro(uebersicht.eingegangen)}</td>
                                </tr>
                              )}
                              {uebersicht.angerechnet !== '0.00' && (
                                <tr>
                                  <td>abzüglich BG-Gutscheine</td>
                                  <td className="wert">{alsEuro(uebersicht.angerechnet)}</td>
                                </tr>
                              )}
                              <tr className="gesamt">
                                <td>Offen</td>
                                <td className="wert">
                                  <strong>{alsEuro(uebersicht.offen)}</strong>
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>

                        {uebersicht.vorbehalte.length > 0 && (
                          <div className="warnung">
                            {uebersicht.vorbehalte.map((v) => (
                              <p key={v}>{v}</p>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                </>
              )}

              {reiter === 'teilnehmer' && (
                <>
                    {eigeneTeilnehmer.length > 0 && (
                      <div className="karte">
                        <h2>Teilnehmer dieser Buchung</h2>
                        <p className="hilfe">
                          Namen können bis zur Frist nachgereicht werden. Eine abgelaufene Frist
                          sperrt die Meldung <strong>nicht</strong> — sie ist eine Mahnungsgrenze,
                          keine Sperre.
                        </p>
                        {/* CRM-106: Die Zeilen tragen Eingaben — auf dem Telefon als Karten. */}
                        <div className="tabellenhuelle tabellenhuelle--telefon">
                          <table className="liste__tabelle tabelle--karten">
                            <caption className="nur-vorlesen">Teilnehmer dieser Buchung</caption>
                            <thead>
                              <tr>
                                <th scope="col">Platz</th>
                                <th scope="col">Name</th>
                                <th scope="col">Gemeldet</th>
                              </tr>
                            </thead>
                            <tbody>
                              {eigeneTeilnehmer.map((tn) => (
                                <tr key={tn.id}>
                                  <td data-spalte="Platz">{tn.position}</td>
                                  <td data-spalte="Name">
                                    {tn.name === null ? (
                                      <input
                                        className="feld__eingabe feld__eingabe--schmal"
                                        aria-label={`Name Platz ${String(tn.position)}`}
                                        value={namen[tn.position] ?? ''}
                                        onChange={(e) => {
                                          setzeNamen({ ...namen, [tn.position]: e.target.value });
                                        }}
                                      />
                                    ) : (
                                      tn.name
                                    )}
                                  </td>
                                  <td className="liste__einzeilig" data-spalte="Gemeldet">
                                    {tn.gemeldetAm === null ? (
                                      <span className="marke marke--warn">Name fehlt</span>
                                    ) : (
                                      datum(tn.gemeldetAm)
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        <div className="knopfreihe knopfreihe--abschluss">
                          <button
                            type="button"
                            className="knopf knopf--haupt"
                            disabled={
                              laeuft || Object.values(namen).every((n) => n.trim() === '')
                            }
                            onClick={() => {
                              void melden();
                            }}
                          >
                            Namen übernehmen
                          </button>
                        </div>
                      </div>
                    )}

                    {teilnehmer.length > 0 && (
                      <Teilnehmerliste
                        zeilen={teilnehmer}
                        terminname={
                          buchung === undefined
                            ? 'Termin'
                            : `${buchung.buchungsnummer} · ${buchung.artikel}, ${buchung.beginn}`
                        }
                      />
                    )}
                  {eigeneTeilnehmer.length === 0 && teilnehmer.length === 0 && (
                    <p className="hinweiszeile">Zu dieser Buchung sind keine Teilnehmer erfasst.</p>
                  )}
                  {buchung !== null && bgGutscheine !== undefined && (
                    <BgGutscheine
                      bezug={{
                        art: 'buchung',
                        id: buchung.id,
                        teilnehmer: eigeneTeilnehmer.map((tn) => ({ id: tn.id, name: tn.name })),
                      }}
                      darfErfassen={bgGutscheine.darfErfassen}
                    />
                  )}
                </>
              )}

              {reiter === 'zusatzleistungen' && (
                <>
                    {/*
                      * ========================================================
                      * ZUSATZLEISTUNGEN (BUC-119, BUC-86, PRD-26)
                      * ========================================================
                      *
                      * Der Bezug wird NICHT gewählt: Er steht am Artikel (BUC-84).
                      * Kaffee je Teilnehmer, Kaffeekanne je Buchung. Die Maske zeigt
                      * das Teilnehmerfeld deshalb nur, wo es hingehört — ein Feld, das
                      * die Datenbank danach abweist, ist eine Falle.
                      */}
                    <div className="karte">
                      <h2>Zusatzleistungen</h2>

                      <div className="feld">
                        <label className="feld__beschriftung" htmlFor="zPreisliste">
                          Preisliste der Zusatzleistungen
                        </label>
                        <select
                          id="zPreisliste"
                          className="feld__eingabe"
                          value={zusatzpreisliste}
                          onChange={(e) => {
                            void preislisteWaehlen(e.target.value as Zusatzpreisliste);
                          }}
                        >
                          <option value="standard">Standard</option>
                          <option value="mitglied">Mitglied</option>
                        </select>
                        {/* BUC-91 NOCH-OFFEN */}
                        <p className="feld__hilfe">
                          Mitglied: Wo die Mitgliederpreisliste einen Preis trägt, gilt er; sonst der
                          Standardpreis. Die Mitgliedschaft wird dabei noch nicht geprüft.
                        </p>
                      </div>

                      {zusatz.length === 0 ? (
                        <p className="hilfe">Noch keine Zusatzleistung gewählt.</p>
                      ) : (
                        <div className="tabellenhuelle tabellenhuelle--telefon">
                          <table className="liste__tabelle tabelle--karten">
                            <caption className="nur-vorlesen">Gewählte Zusatzleistungen</caption>
                            <thead>
                              <tr>
                                <th scope="col">Leistung</th>
                                <th scope="col">Für</th>
                                <th scope="col">Menge</th>
                                <th scope="col">Einzelpreis</th>
                                <th scope="col" />
                              </tr>
                            </thead>
                            <tbody>
                              {zusatz.map((z) => (
                                <tr key={z.id}>
                                  <td data-spalte="Leistung">
                                    {z.bezeichnung}
                                    {z.nachgebucht && (
                                      <>
                                        {' '}
                                        <span className="marke marke--warn">nachgebucht</span>
                                      </>
                                    )}
                                  </td>
                                  <td data-spalte="Für">
                                    {z.teilnehmer === null ? (
                                      <span className="hilfe">die ganze Buchung</span>
                                    ) : (
                                      `Platz ${String(z.position ?? '?')} — ${z.teilnehmername ?? 'ohne Namen'}`
                                    )}
                                  </td>
                                  <td data-spalte="Menge">{z.menge}</td>
                                  <td data-spalte="Einzelpreis">
                                    {z.einzelpreis === null ? '— kein Preis' : alsEuro(z.einzelpreis)}
                                  </td>
                                  <td>
                                    <button
                                      type="button"
                                      className="knopf knopf--eingriff knopf--klein"
                                      onClick={() => {
                                        setzeNachfrage({ art: 'zusatz', leistung: z });
                                      }}
                                    >
                                      Stornieren …
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}

                      {/*
                        * CRM-106: Auf dem Telefon öffnet die Wahl im Dialog. Der Knopf
                        * bleibt stehen, solange er offen ist — dorthin kehrt der Fokus
                        * zurück. Am Desktop stehen die Felder wie bisher unter der Liste.
                        */}
                      {telefon && (
                        <div className="knopfreihe">
                          <button
                            type="button"
                            className="knopf knopf--klein"
                            onClick={() => {
                              setzeZFensterOffen(true);
                            }}
                          >
                            Zusatzleistung hinzufügen …
                          </button>
                        </div>
                      )}
                      {(!telefon || zFensterOffen) && (
                        <Telefonfenster
                          titel="Zusatzleistung hinzufügen"
                          unterzeile={buchung.buchungsnummer}
                          beiSchliessen={() => {
                            setzeZFensterOffen(false);
                          }}
                        >
                          {telefon && fehler !== null && <Meldung fehler={fehler} />}
                          <div className="feld">
                            <label className="feld__beschriftung" htmlFor="zArtikel">
                              Zusatzleistung
                            </label>
                            <select
                              id="zArtikel"
                              className="feld__eingabe"
                              value={zArtikel}
                              onChange={(e) => {
                                setzeZArtikel(e.target.value);
                                setzeZTeilnehmer('');
                              }}
                            >
                              <option value="">— bitte wählen —</option>
                              {zusatzangebot.map((a) => (
                                // BUC-85: Was ausgebucht ist, steht da, lässt sich aber nicht wählen.
                                <option key={a.id} value={a.id} disabled={a.frei === 0}>
                                  {a.bezeichnung}
                                  {a.frei === null ? '' : a.frei === 0 ? ' — ausgebucht' : ` — ${a.frei} frei`}
                                </option>
                              ))}
                            </select>
                            {zusatzangebot.length === 0 && (
                              <p className="feld__hilfe">
                                Es ist kein Artikel mit der Vertriebsform „Zusatzleistung" gepflegt.
                              </p>
                            )}
                          </div>

                          {zArtikel !== '' && (
                            <div className="feld">
                              <label className="feld__beschriftung" htmlFor="zTeilnehmer">
                                Für
                              </label>
                              <select
                                id="zTeilnehmer"
                                className="feld__eingabe"
                                value={zTeilnehmer}
                                onChange={(e) => {
                                  setzeZTeilnehmer(e.target.value);
                                }}
                              >
                                <option value="">die ganze Buchung</option>
                                {eigeneTeilnehmer.map((tn) => (
                                  <option key={tn.id} value={tn.id}>
                                    Platz {tn.position} — {tn.name ?? 'ohne Namen'}
                                  </option>
                                ))}
                              </select>
                              <p className="feld__hilfe">
                                Ob eine Leistung je Buchung oder je Teilnehmer gilt, steht am Artikel.
                                Passt die Wahl nicht, wird sie abgewiesen.
                              </p>
                            </div>
                          )}

                          {/*
                            * BUC-86: Was dagegen spricht, steht VOR dem Knopf — und der
                            * Knopf bleibt aus. FRS-15: nicht anbieten, was ohnehin
                            * abgewiesen würde.
                            */}
                          {zPruefung !== null && zPruefung.einwaende.length > 0 && (
                            <div className="warnung">
                              {zPruefung.einwaende.map((e) => (
                                <p key={`${e.art}-${e.anderer}`}>
                                  {e.begruendung}{' '}
                                  {e.art === 'setzt_voraus'
                                    ? `Wählen Sie zuerst „${e.andererName}".`
                                    : `Entfernen Sie zuerst „${e.andererName}".`}
                                </p>
                              ))}
                            </div>
                          )}

                          {zPruefung !== null && zPruefung.verdraengt.length > 0 && (
                            <p className="hinweiszeile" role="status">
                              {zPruefung.verdraengt
                                .map((v) => `„${v.bezeichnung}" fällt weg: ${v.begruendung}`)
                                .join(' ')}
                            </p>
                          )}

                          <div className="knopfreihe">
                            <button
                              type="button"
                              className="knopf knopf--klein"
                              disabled={
                                zArtikel === ''
                                || (zPruefung !== null && zPruefung.einwaende.length > 0)
                              }
                              onClick={() => {
                                void zusatzWaehlen().then((gewaehlt) => {
                                  if (gewaehlt) setzeZFensterOffen(false);
                                });
                              }}
                            >
                              Zusatzleistung hinzufügen
                            </button>
                          </div>
                        </Telefonfenster>
                      )}
                    </div>
                </>
              )}

              {/*
                * STORNO UND UMBUCHUNG BLEIBEN EINE ABFOLGE (BUC-45): Vorgang,
                * darunter die Berechnung, danach der Beleg — in einem Reiter,
                * damit die Zahl im Blick bleibt, während man ausführt.
                */}
              {reiter === 'storno' && (
                <>
                  {/* Auftrag CRM-95: stand über der Akte, gilt aber diesem Reiter. */}
                  <p className="hilfe">
                    Storno, Umbuchung und Teilnehmerwechsel. Die Gebühr wird angezeigt, bevor etwas
                    geschieht — bis zum Knopf „Ausführen" wird kein Platz bewegt.
                  </p>
                  {flextarifmarke !== null && (
                    <p className="hinweiszeile">Flextarif: {flextarifmarke}</p>
                  )}
                    <div className="karte">
                      <h2>Vorgang</h2>
                      <div className="feld">
                        <label className="feld__beschriftung" htmlFor="art">
                          Vorgangsart
                        </label>
                        <select
                          id="art"
                          className="feld__eingabe"
                          value={art}
                          onChange={(e) => {
                            setzeArt(e.target.value as Vorgangsart);
                          }}
                        >
                          {(['storno', 'umbuchung', 'teilnehmerwechsel'] as const).map((a) => (
                            <option key={a} value={a}>
                              {VORGANGSNAME[a]}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="feld">
                        <label className="feld__beschriftung" htmlFor="zugangAm">
                          Zugang der Erklärung
                        </label>
                        <input
                          id="zugangAm"
                          type="date"
                          className="feld__eingabe"
                          value={zugangAm}
                          max={HEUTE()}
                          onChange={(e) => {
                            setzeZugangAm(e.target.value);
                          }}
                        />
                        <p className="feld__hilfe">
                          Wann die Erklärung eingegangen ist — nicht wann sie erfasst wird. Sie entscheidet
                          über die Stufe der Staffel.
                        </p>
                      </div>

                      {/* BUC-144 (AeA-59): der Nachweis, wie die Erklärung einging — wie im Vertrieb. */}
                      {art === 'storno' && (
                        <Herkunftsfelder
                          id="erklaerung"
                          weg={herkunft.weg}
                          verweis={herkunft.verweis}
                          beiAenderung={setzeHerkunft}
                        />
                      )}

                      <div className="feld">
                        <label className="feld__beschriftung" htmlFor="plaetze">
                          Betroffene Plätze
                        </label>
                        <input
                          id="plaetze"
                          type="number"
                          min={1}
                          max={buchung.plaetze}
                          className="feld__eingabe"
                          value={plaetze}
                          onChange={(e) => {
                            setzePlaetze(e.target.value);
                          }}
                        />
                      </div>

                      {auswahlNoetig && (
                        <fieldset className="feld">
                          <legend className="feld__beschriftung">
                            {art === 'storno' ? 'Wer nimmt nicht teil?' : 'Wer wechselt den Termin?'}
                          </legend>
                          {eigeneTeilnehmer.map((tn) => (
                            <label key={tn.id} className="liste__schalter">
                              <input
                                type="checkbox"
                                checked={auswahl.includes(tn.id)}
                                onChange={(e) => {
                                  setzeAuswahl(
                                    e.target.checked ? [...auswahl, tn.id] : auswahl.filter((a) => a !== tn.id),
                                  );
                                }}
                              />
                              <span>
                                Platz {tn.position} — {tn.name ?? 'ohne Namen'}
                              </span>
                            </label>
                          ))}
                          <p className="feld__hilfe" role={auswahl.length > 0 && auswahl.length !== Number(plaetze) ? 'alert' : undefined}>
                            {auswahl.length === 0
                              ? 'Ohne Auswahl sind zuerst die Plätze ohne Namen betroffen, dann die mit der höchsten Nummer.'
                              : auswahl.length === Number(plaetze)
                                ? `${String(auswahl.length)} gewählt.`
                                : `${String(auswahl.length)} gewählt — es müssen genau ${plaetze} sein.`}
                          </p>
                        </fieldset>
                      )}

                      {art === 'umbuchung' && (
                        <div className="feld">
                          <label className="feld__beschriftung" htmlFor="zielTermin">
                            Zieltermin <span className="feld__pflicht">Pflicht</span>
                          </label>
                          {/* CRM-94: gewählt wird im Kalender wie bei der neuen Buchung. */}
                          <div className="wahlfeld">
                            <input
                              id="zielTermin"
                              className="feld__eingabe"
                              readOnly
                              aria-invalid={zielZuKlein || undefined}
                              value={
                                ziel === undefined
                                  ? ''
                                  : `${new Date(ziel.beginn).toLocaleString('de-DE', { timeZone: 'Europe/Berlin' })} — `
                                    + `${String(ziel.frei)} von ${String(ziel.hoechstteilnehmer)} frei`
                              }
                              placeholder="— noch keiner gewählt —"
                              onClick={() => {
                                if (ziele.length > 0) setzeFenster('zieltermin');
                              }}
                            />
                            <button
                              type="button"
                              className="knopf"
                              disabled={ziele.length === 0}
                              onClick={() => {
                                setzeFenster('zieltermin');
                              }}
                            >
                              {ziel === undefined ? 'Termin wählen…' : 'Ändern'}
                            </button>
                          </div>
                          {/* FRS-15: Reicht der gewählte Termin nach dem Ändern der Plätze nicht mehr, steht es da. */}
                          {ziel !== undefined && zielZuKlein && (
                            <p className="feld__hilfe" role="alert">
                              Dieser Termin hat nur {ziel.frei} {ziel.frei === 1 ? 'freien Platz' : 'freie Plätze'} —
                              zu wenig für {umziehend} {umziehend === 1 ? 'Platz' : 'Plätze'}. Bitte einen anderen
                              wählen.
                            </p>
                          )}
                          <p className="feld__hilfe">
                            {ziele.length === 0
                              ? 'Für dieses Produkt gibt es keinen weiteren buchbaren Termin.'
                              : 'Nur buchbare Termine desselben Produkts. „Frei" ist die maßgebliche '
                                + 'Kapazität — Termine mit zu wenig Platz sind nicht wählbar.'}
                          </p>
                        </div>
                      )}

                      {/*
                       * Der Grundbetrag ist nur nötig, wenn die Buchung an keiner
                       * Auftragsposition hängt (BUC-50, Direktbuchung). Sonst kommt er
                       * von dort, und ein Eingabefeld lüde nur zum Übersteuern ein.
                       */}
                      {buchung.auftragsposition === null && (
                        <div className="feld">
                          <label className="feld__beschriftung" htmlFor="grundbetrag">
                            Grundbetrag <span className="feld__pflicht">Pflicht</span>
                          </label>
                          <input
                            id="grundbetrag"
                            className="feld__eingabe"
                            value={grundbetrag}
                            onChange={(e) => {
                              setzeGrundbetrag(e.target.value);
                            }}
                            placeholder="4000.00"
                          />
                          <p className="feld__hilfe">
                            Diese Buchung hängt an keiner Auftragsposition. Nennen Sie den vereinbarten
                            Preis der betroffenen Positionen.
                          </p>
                        </div>
                      )}

                      <div className="feld">
                        <label className="feld__beschriftung" htmlFor="grund">
                          Grund
                        </label>
                        <input
                          id="grund"
                          className="feld__eingabe"
                          value={grund}
                          onChange={(e) => {
                            setzeGrund(e.target.value);
                          }}
                        />
                      </div>
                    </div>

                    {vorschau !== null && (
                      <div className="karte" role="status">
                        <h2>Berechnung</h2>
                        {vorschau.nichtVorgesehen ? (
                          <p>
                            <span className="marke marke--warn">nicht vorgesehen</span> Ein{' '}
                            {VORGANGSNAME[vorschau.art]} ist bei {vorschau.abstandInTagen} Tagen Abstand
                            nach Staffel {vorschau.staffel} ausgeschlossen.
                            {vorschau.art === 'umbuchung'
                              ? ' Ein Terminwechsel läuft hier als Storno mit Neubuchung.'
                              : ''}
                          </p>
                        ) : (
                          <>
                            <div className="tabellenhuelle tabellenhuelle--telefon">
                              <table className="liste__tabelle">
                                <caption className="nur-vorlesen">Berechnung der Gebühr</caption>
                                <tbody>
                                  <tr>
                                    <th scope="row">Grundbetrag</th>
                                    <td>{alsEuro(vorschau.grundbetrag)}</td>
                                  </tr>
                                  <tr>
                                    <th scope="row">
                                      Gebühr
                                      {vorschau.satzProzent !== null && (
                                        <span className="hilfe">
                                          {' '}
                                          {Number(vorschau.satzProzent).toLocaleString('de-DE')} % ab{' '}
                                          {vorschau.stufeAbTagen} Tagen, Staffel {vorschau.staffel}
                                        </span>
                                      )}
                                    </th>
                                    <td>{alsEuro(vorschau.gebuehr)}</td>
                                  </tr>
                                  <tr>
                                    <th scope="row">Bereits gezahlt, verrechnet</th>
                                    <td>{alsEuro(vorschau.verrechnet)}</td>
                                  </tr>
                                  <tr>
                                    <th scope="row">
                                      {Number(vorschau.zuErstatten) > 0 ? 'Zu erstatten' : 'Zu berechnen'}
                                    </th>
                                    <td>
                                      <strong>
                                        {alsEuro(
                                          Number(vorschau.zuErstatten) > 0
                                            ? vorschau.zuErstatten
                                            : vorschau.zuBerechnen,
                                        )}
                                      </strong>
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                            <p className="hilfe">
                              Abstand zum Termin: {vorschau.abstandInTagen} Kalendertage, gerechnet ab dem
                              Zugang der Erklärung.
                            </p>
                            {vorschau.flextarifGenutzt && (
                              <p>
                                <span className="marke marke--gut">Flextarif</span> Diese Umbuchung ist
                                kostenfrei. Das Recht gilt danach als verbraucht.
                              </p>
                            )}
                            {vorschau.hinweise.map((h) => (
                              <p key={h} className="feld__fehler-satz">
                                {h}
                              </p>
                            ))}
                          </>
                        )}

                        <div className="knopfreihe knopfreihe--abschluss">
                          <button
                            type="button"
                            className={art === 'storno' ? 'knopf knopf--eingriff' : 'knopf knopf--haupt'}
                            disabled={gesperrt}
                            onClick={() => {
                              setzeNachfrage({ art: 'ausfuehren' });
                            }}
                          >
                            {VORGANGSNAME[art]} ausführen …
                          </button>
                        </div>
                      </div>
                    )}

                    {erledigt !== null && (
                      <div className="karte" role="status">
                        <span className="marke marke--gut">ausgeführt</span> Der Vorgang ist festgehalten.
                        Der Beleg dazu wird in der Faktura erzeugt.
                      </div>
                    )}

                    {/* Danach der Beleg (RNG-03) — die Vorgänge, die noch keinen haben. */}
                    {historie.some((v) => v.beleg === null) && (
                      <div className="karte">
                        <h2>Beleg zum Vorgang</h2>
                        {vorgangstabelle(historie.filter((v) => v.beleg === null))}
                      </div>
                    )}

                    {belegkarte}
                </>
              )}

              {reiter === 'beleg' && (
                <>
                    {/*
                      * ========================================================
                      * DIE BELEGSEITE DER BUCHUNG (BUC-121, WEB-12)
                      * ========================================================
                      *
                      * DER BEFUND VOM 16.09.2026 (Durchlauf Privatkundenprozess,
                      * Befund 3): „POST /buchungen/:id/belegpositionen setzt BUC-121 um.
                      * KEINE MASKE RUFT IHN AUF." Und seit demselben Tag gibt es einen
                      * Zahlungslink, den ebenfalls niemand rufen konnte.
                      *
                      * Die Leitung lag, die Hähne fehlten. Hier sind sie.
                      *
                      * DER AUFTRAG STEHT ZUERST, weil er das Einzige ist, was aus einer
                      * Buchung eine Forderung macht: Wer die Rechnung sucht, sucht sie
                      * über die Auftragsnummer.
                      */}
                    {buchung !== null && (
                      <div className="karte">
                        <h2>Beleg und Zahlung</h2>
                        <dl className="angaben">
                          <dt>Auftrag</dt>
                          <dd>
                            {buchung.auftragsnummer === null ? (
                              <span className="marke marke--warn">keiner</span>
                            ) : (
                              <>
                                <Aktenverweis ziel="auftrag" id={buchung.auftrag} wege={wege}>
                                  {buchung.auftragsnummer}
                                </Aktenverweis>
                                {buchung.auftragsstatus !== null && (
                                  <span className="kanalliste__name">
                                    {' · '}
                                    {/*
                                      DAS WORT UND NICHT DER SCHLUESSELWERT (Hinweis 11
                                      der Maskenpruefung): „bestaetigt" ist kein
                                      deutsches Wort, „bestätigt" schon.
                                    */}
                                    {AUFTRAGSSTATUSNAME[buchung.auftragsstatus as Auftragsstatus]
                                      ?? buchung.auftragsstatus}
                                  </span>
                                )}
                              </>
                            )}
                          </dd>
                          <dt>Belegzeilen</dt>
                          <dd>
                            {buchung.belegzeilen === 0
                              ? 'noch keine — die Sammelposition trägt den Betrag'
                              : `${String(buchung.belegzeilen)} je Teilnehmer`}
                          </dd>
                          <dt>Zahlung</dt>
                          <dd>{zahlungstext(buchung.zahlung)}</dd>
                          {faktura !== null && faktura.stand !== 'ausgenommen' && (
                            <>
                              <dt>Rechnung</dt>
                              <dd>{fakturatext(faktura)}</dd>
                            </>
                          )}
                        </dl>

                        {faktura !== null && faktura.meldung !== null && (
                          <p className="feld__hilfe">{faktura.meldung}</p>
                        )}

                        {buchung.auftragsnummer === null ? (
                          <p className="feld__hilfe">
                            {faktura === null && buchung.herkunft === 'portal'
                              ? 'Eine Webbuchung bekommt Auftrag und Rechnung von selbst — '
                                + 'sobald die Zahlung entschieden ist, frühestens zehn Minuten nach dem '
                                + 'Abschluss.'
                              : faktura === null || faktura.stand === 'ausgenommen'
                                ? 'Diese Buchung hängt an keinem Auftrag. Sie stammt aus der Zeit vor dem '
                                  + '16.09.2026 (Webbuchungen: vor dem 19.09.2026). Ohne Auftrag gibt es '
                                  + 'keinen Beleg und keine Stornogebühr aus dem Preis.'
                                : 'Der Auftrag konnte nicht angelegt werden; der Grund steht oben. Nach dem '
                                  + 'fünften Versuch bleibt die Buchung bei Ihnen.'}
                          </p>
                        ) : (
                          <div className="knopfreihe">
                            <button
                              type="button"
                              className="knopf knopf--klein"
                              disabled={laeuft}
                              onClick={() => {
                                // CRM-103: Nachgefragt wird nur, wo Vorhandenes überschrieben wird.
                                if (buchung.belegzeilen === 0) void aufDieSeite(belegzeilenErzeugen);
                                else setzeNachfrage({ art: 'belegzeilen' });
                              }}
                            >
                              {buchung.belegzeilen === 0
                                ? 'Belegpositionen erzeugen'
                                : 'Belegpositionen neu erzeugen …'}
                            </button>
                            <button
                              type="button"
                              className="knopf knopf--klein"
                              disabled={laeuft || uebersicht === null || nichtsOffen(uebersicht)}
                              title={uebersicht !== null && nichtsOffen(uebersicht) ? 'bereits bezahlt' : undefined}
                              onClick={() => void linkErzeugen()}
                            >
                              Zahlungslink erzeugen
                            </button>
                          </div>
                        )}
                        {/* RNG-27 (AP-FA-03): Ist nichts offen, gibt es keinen Link. */}
                        {uebersicht !== null && nichtsOffen(uebersicht) && (
                          <p className="feld__hilfe">Zahlungslink: bereits bezahlt, es ist nichts mehr offen.</p>
                        )}

                        <p className="feld__hilfe">
                          <strong>Belegpositionen</strong> machen aus der Übersicht je Teilnehmer eine
                          Zeile am Auftrag — mit abweichenden Preisen, wo es welche gibt. Sie lösen die
                          Sammelposition ab; der Betrag steht danach nicht zweimal.{' '}
                          <strong>Der Zahlungslink</strong> erzeugt den Vorgang beim Dienstleister; bezahlt
                          ist erst, was dessen Bestätigung meldet.
                        </p>
                      </div>
                    )}
                </>
              )}

              {reiter === 'verlauf' && (
                <>
                    {historie.length > 0 && (
                      <div className="karte">
                        <h2>Bisherige Vorgänge</h2>
                        {vorgangstabelle(historie)}
                      </div>
                    )}
                  {historie.length === 0 && (
                    <p className="hinweiszeile">Zu dieser Buchung gibt es noch keine Vorgänge.</p>
                  )}
                  {belegkarte}
                </>
              )}

              {reiter === 'bedingungen' && (
                <>
                    {/*
                      * ========================================================
                      * RECHTSTEXTE UND ZUSTIMMUNG (BUC-25, BUC-123)
                      * ========================================================
                      *
                      * Die Karte steht VOR den Teilnehmern und nicht am Ende: Was hier
                      * offen ist, hält die Buchung auf. Wer sie erst nach dem Scrollen
                      * fände, hätte den Vorgang längst für erledigt gehalten.
                      */}
                    {rechtsstand !== null && (
                      <div className="karte">
                        <h2>Bedingungen und Zustimmung</h2>

                        {rechtsstand.platzhalter.length > 0 && (
                          <p className="warnung">
                            {rechtsstand.platzhalter.length === 1
                              ? 'Ein Text ist noch ein Platzhalter'
                              : `${String(rechtsstand.platzhalter.length)} Texte sind noch Platzhalter`}{' '}
                            ({rechtsstand.platzhalter.map((x) => x.bezeichnung).join(', ')}). Solange das
                            so ist, geht keine Bestätigung an den Kunden hinaus. Die Fassungen stehen in
                            der Verwaltung unter „Rechtstexte".
                          </p>
                        )}

                        <div className="tabellenhuelle tabellenhuelle--telefon">
                          <table className="liste__tabelle">
                            <caption className="nur-vorlesen">Geltende Rechtstexte</caption>
                            <thead>
                              <tr>
                                <th scope="col">Text</th>
                                <th scope="col">Fassung</th>
                                <th scope="col">Stand</th>
                              </tr>
                            </thead>
                            <tbody>
                              {rechtstexte.map((x) => {
                                const nochOffen = rechtsstand.offen.some((o) => o.id === x.id);
                                return (
                                  <tr key={x.id}>
                                    <td>
                                      {x.bezeichnung}
                                      {x.platzhalter && (
                                        <>
                                          {' '}
                                          <span className="marke marke--warn">Platzhalter</span>
                                        </>
                                      )}
                                    </td>
                                    <td className="liste__nummer">{x.kennung}</td>
                                    <td>
                                      {/*
                                        * BUC-25: Zugestimmt wird den AGB und den
                                        * Bedingungen. Eine Widerrufsbelehrung wird ZUR
                                        * KENNTNIS GENOMMEN — sie ist eine Belehrung und
                                        * keine Vereinbarung, und eine protokollierte
                                        * „Zustimmung" dazu wäre ein Vorgang, den es
                                        * rechtlich nicht gibt.
                                        */}
                                      {!x.zustimmungspflichtig ? (
                                        <span className="hilfe">zur Kenntnis</span>
                                      ) : nochOffen ? (
                                        <button
                                          type="button"
                                          className="knopf knopf--klein"
                                          onClick={() => {
                                            void zustimmen(x.id);
                                          }}
                                        >
                                          Zustimmung vermerken
                                        </button>
                                      ) : (
                                        <span className="marke marke--gut">zugestimmt</span>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                              {rechtstexte.length === 0 && (
                                <tr>
                                  <td colSpan={3}>
                                    Für diese Buchung ist keine Fassung hinterlegt.
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>

                        {/*
                          * BUC-123: Der Anmelder gibt Daten Dritter an.
                          *
                          * Beim Firmenkunden ist das der Regelfall — fünf Namen, keiner
                          * davon er selbst. Beim Verbraucher ebenso, sobald er für
                          * jemand anderen mitbucht.
                          */}
                        <div className="feld">
                          {rechtsstand.datenDritterOffen ? (
                            <button
                              type="button"
                              className="knopf knopf--klein"
                              onClick={() => {
                                setzeNachfrage({ art: 'bestaetigung', gegenstand: 'daten_dritter' });
                              }}
                            >
                              Berechtigung für Teilnehmerdaten bestätigen …
                            </button>
                          ) : (
                            <span className="marke marke--gut">Teilnehmerdaten bestätigt</span>
                          )}
                          <p className="feld__hilfe">
                            Der Anmelder bestätigt, dass er die Daten der Teilnehmer angeben darf und sie
                            informiert hat.
                          </p>
                        </div>

                        {/*
                          * ========================================================
                          * DIE UEBERMITTLUNG (BUC-78)
                          * ========================================================
                          *
                          * „Bei telefonischer Buchung durch Verbraucher werden AGB,
                          * Teilnahme-, Storno- und Widerrufsbedingungen sowie die
                          * Pflichtangaben UNMITTELBAR NACH DEM GESPRÄCH in Textform
                          * übermittelt."
                          *
                          * Wer welche Fassung bekäme, steht VOR dem Knopf: Ein Versand,
                          * dessen Empfänger man erst hinterher erfährt, lässt sich
                          * nicht zurücknehmen.
                          */}
                        {versandbild !== null && (
                          <div className="feld">
                            <p className="feld__hilfe">
                              Beim Verschicken gehen die Texte an:{' '}
                              {[...new Set(versandbild.zeilen.map((z) => z.empfaenger))].join(', ') || '—'}
                            </p>
                            {/*
                              * 0077: Solange der Probeversand an ist, geht Unfertiges
                              * hinaus — und der Bearbeiter, der auf den Knopf drückt,
                              * muss es wissen, bevor er drückt.
                              */}
                            {versandbild.probeversand && (
                              <p className="warnung">
                                <strong>Probeversand eingeschaltet.</strong> Es gehen Platzhalter und
                                Fassungen ohne Datei hinaus. Die Nachricht trägt einen Hinweis und zählt
                                nicht als Übermittlung — sie muss später wiederholt werden.
                              </p>
                            )}
                            {versandbild.rueckfallAufAnmelder && (
                              <p className="warnung">
                                Für die Teilnehmer ist keine E-Mail-Adresse hinterlegt. Die
                                Teilnehmerunterlagen gehen deshalb an den Anmelder — er gibt sie weiter.
                              </p>
                            )}
                            {versandbild.ohneAdresse.length > 0 && (
                              <p className="warnung">
                                Ohne Empfänger: {versandbild.ohneAdresse.join(', ')}. Tragen Sie am Kunden
                                eine E-Mail-Adresse ein.
                              </p>
                            )}
                            <div className="knopfreihe">
                              <button
                                type="button"
                                className="knopf knopf--klein"
                                disabled={
                                  versandbild.zeilen.length === 0
                                  || (rechtsstand.platzhalter.length > 0 && !versandbild.probeversand)
                                }
                                onClick={() => {
                                  setzeNachfrage({ art: 'versand', anlass: 'buchung' });
                                }}
                              >
                                Bedingungen verschicken …
                              </button>
                              <button
                                type="button"
                                className="knopf knopf--klein"
                                disabled={
                                  versandbild.zeilen.length === 0
                                  || (rechtsstand.platzhalter.length > 0 && !versandbild.probeversand)
                                }
                                onClick={() => {
                                  setzeNachfrage({ art: 'versand', anlass: 'erneut' });
                                }}
                              >
                                Erneut verschicken …
                              </button>
                            </div>
                            {versandmeldung !== null && (
                              <p className="hinweiszeile" role="status">
                                {versandmeldung}
                              </p>
                            )}
                          </div>
                        )}

                        {/*
                          * § 356 Abs. 4 BGB — und bei diesem Haus der Regelfall.
                          *
                          * Wer heute für in zehn Tagen bucht, liegt in der
                          * Vierzehntagefrist. Ohne diese Zustimmung kann der Verbraucher
                          * NACH dem absolvierten Training widerrufen und den vollen
                          * Preis zurückverlangen.
                          *
                          * Das Feld erscheint nur, wenn es ansteht: Eine Zustimmung
                          * ohne Anlass ist kein Nachweis, sondern ein Eintrag, der im
                          * Streitfall gegen das Haus spricht.
                          */}
                        {rechtsstand.vorzeitigerBeginnOffen && (
                          <div className="feld">
                            <p className="warnung">
                              Der Termin liegt innerhalb der Widerrufsfrist. Ohne ausdrückliche Zustimmung
                              zum vorzeitigen Leistungsbeginn kann der Kunde auch nach dem Training noch
                              widerrufen (§ 356 Abs. 4 BGB).{/* BUC-25 */}
                            </p>
                            <button
                              type="button"
                              className="knopf knopf--klein"
                              onClick={() => {
                                setzeNachfrage({ art: 'bestaetigung', gegenstand: 'vorzeitiger_beginn' });
                              }}
                            >
                              Vorzeitigen Leistungsbeginn bestätigen …
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  {rechtsstand === null && (
                    <p className="hinweiszeile">
                      Kein Stand der Zustimmungen — er wird geladen oder konnte nicht abgerufen werden.
                    </p>
                  )}
                </>
              )}
            </div>
          </section>
        </div>
      )}
    </div>
  );
}

/** Betrifft der Vorgang nur einen Teil der Plaetze? */
function teilweise(gebucht: number, anzahl: number): boolean {
  return Number.isInteger(anzahl) && anzahl > 0 && anzahl < gebucht;
}

/**
 * Wie viele Plätze eine Umbuchung mitnimmt (BUC-51). Ohne gültige Eingabe
 * geht der Aufruf ohne `plaetze` hinaus, und der Server nimmt alle.
 */
function umziehendePlaetze(gebucht: number, eingabe: string): number {
  const anzahl = Number(eingabe);
  return Number.isInteger(anzahl) && anzahl > 0 ? anzahl : gebucht;
}

/** RNG-27 (AP-FA-03): Der Server liefert nie weniger als 0,00 — „nichts offen" ist genau dieser Wert. */
function nichtsOffen(uebersicht: Buchungsuebersicht): boolean {
  return uebersicht.offen === '0.00';
}
